import { AddAggregateColumnDialog } from "./components/AddAggregateColumnDialog";
import ClearableSearchField from "./components/ClearableSearchField";
import { ColumnHeaderMenu } from "./components/ColumnHeaderMenu";
import { RenameColumnDialog } from "./components/Dialogs";
import { LoadingOverlay } from "./components/LoadingOverlay";
import NameDialog from "./components/NameDialog";
import { PaginationControls } from "./components/PaginationControls";
import { RangeGroupingDialog } from "./components/RangeGroupingDialog";
import { Toolbar } from "./components/Toolbar";
import { ColumnsButton } from "./components/toolbar/ColumnsButton";
import { FiltersButton } from "./components/toolbar/FiltersButton";
import LayoutMenu from "./components/toolbar/LayoutMenu";
import { MetricsButton } from "./components/toolbar/MetricsButton";
import { SaveDropdown } from "./components/toolbar/SaveDropdown";
import { ViewModeToggle } from "./components/toolbar/ViewModeToggle";
import ColumnFilterPanel from "./filters/ColumnFilterPanel";
import { useAddFilterPanel } from "./hooks/useAddFilterPanel";
import { useColumnDragDrop } from "./hooks/useColumnDragDrop";
import { useColumnCounts, useFilterStats } from "./hooks/useColumnFilterCounts";
import { useColumnManagement } from "./hooks/useColumnManagement";
import { useColumnMenu } from "./hooks/useColumnMenu";
import { useColumnPinning } from "./hooks/useColumnPinning";
import { useDataGridStyles } from "./hooks/useDataGridStyles";
import { useEnumDisplayValues } from "./hooks/useEnumDisplayValues";
import { useExportReport } from "./hooks/useExportReport";
import { useInputFilterSummary } from "./hooks/useInputFilterSummary";
import { useLocalStorageSync } from "./hooks/useLocalStorageSync";
import { usePagination } from "./hooks/usePagination";
import { useRangeGrouping } from "./hooks/useRangeGrouping";
import { useReportExportContext } from "./hooks/useReportExportContext";
import { useReportStateContext } from "./hooks/useReportState";
import { useSaveReport, buildRequestSpec } from "./hooks/useSaveReport";
import { useViewMode } from "./hooks/useViewMode";
import { useXlsxDownload } from "./hooks/useXlsxDownload";
import {
  createEnumChipRenderer,
  createSubmissionNameRenderer,
  getColumnTypeProps,
  renderSubmissionIdCell,
  renderTargetPriceCell,
} from "./renderers/cellRenderers";
import {
  createHeaderRenderer,
  GROUP_HEADER_ROW_HEIGHT_PX,
} from "./renderers/headerRenderers";
import * as styles from "./styles";
import {
  GROUP_ROW_DETAIL,
  GROUP_ROW_GRAND_TOTAL,
  GROUP_ROW_GROUP,
  GROUP_ROW_SUB_TOTAL,
  INPUT_COLUMN_FILTER_CHANGE_TYPE,
  INPUT_DATE_FILTER_CHANGE_TYPE,
  INPUT_NUMBER_FILTER_CHANGE_TYPE,
  CELL_ALIGN_CLASS_PREFIX,
  ENUM_BUTTONS_SUB_TYPE,
  ENUM_STATUS_BUTTONS_SUB_TYPE,
  FILTER_KIND_INPUT,
  FILTER_KIND_OUTPUT,
  FILTER_MAP_KEYS,
  FILTER_SELECT_COLUMN,
  REPORT_CONFIGURATION_NAME,
  SUMMARY_ROW,
  DEFAULT_COLUMN_WIDTH_PX,
  DEFAULT_EXTRA_COLUMN_WIDTH_PX,
} from "./types/constants";
import { useGroupAggregators } from "./utils/aggregationUtils";
import {
  getColumnOrderDiff,
  orderPinnedByColumnOrder,
} from "./utils/columnOrderUtils";
import { buildConfigurationPayload as buildConfigurationPayloadUtil } from "./utils/configurationUtils";
import {
  DEFAULT_DATE_FORMAT,
  formatAsAtDate,
  formatDateValue,
  getDynamicDateDisplayName,
  isDateRangeSet,
  TREAT_TODAY_AS_HELP_DEFAULT,
} from "./utils/dateUtils";
import {
  buildFilteredColumnSet,
  buildInputFilterSummaryReport,
  chipsByFilterKind,
  columnFilterNumberFormatter,
  filterKindForChangeType,
  filterKindLabels,
  filterKindsForColumn,
  filterValuesPending,
  resolveFilterSource,
} from "./utils/filterKindUtils";
import { toggleSelectedValues } from "./utils/filterSpecUtils";
import {
  selectValueFormatter,
  blankGroupValueFormatter,
  blankGroupRenderer,
  defaultTypeValueFormatter,
  detailRowAware,
  detailRowAwareCellClassName,
  detailRowAwareRenderer,
  isDetailRow,
  rangeGroupedValueFormatter,
  targetPercentageFractionDigitOptions,
} from "./utils/gridValueFormatters";
import { inputColumnView } from "./utils/inputColumnUtils";
import {
  deleteNamedLayout,
  loadLayoutNames,
  saveNamedLayout,
  setActiveLayout,
} from "./utils/localLayout";
import { buildMetricItem } from "./utils/metricsUtils";
import {
  describeRangeGroupedColumn,
  formatStoredThreshold,
  isGroupingColumn,
  rangeGroupedCellValue,
  rewriteOrderForRangeGroupings,
} from "./utils/rangeGroupingUtils";
import {
  resolveSubmissionLayerIndex,
  buildColumnTypeMap,
  buildMetadataDisplayNameMap,
  resolveColumnDisplayName,
  buildColumnDisplayNameMap,
  buildGridRows,
  checkHasDetailRows,
  buildSourceDisplayNameMap,
  collectExistingColumnNames,
  formatSortItems,
  formatMetricItems,
  formatGroupByItems,
  describeColumnWidthChanges,
  formatRangeFilter,
  resolveColumnDecimals,
} from "./utils/reportResultsUtils";
import {
  DASHBOARD_REPORT_ID,
  ensureMetricColumns,
} from "./utils/submissionStatusSpec";
import { buildSpecFromTabState } from "./utils/tabSpecBuilder";
import {
  ChangeCircle as ChangeCircleIcon,
  PriorityHighRounded as FailedIcon,
  PushPin as PushPinIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import * as logger from "common/logger";
import DefaultLoader from "components/common/DefaultLoader";
import DownloadButton from "components/common/DownloadButton";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import StatusSelectWithChecks from "components/common/StatusSelectWithChecks";
import { reportsV3DynamicDatePresets } from "config";
import * as labels from "config.labels";
import React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as reportsV3Service from "services/reportsV3Service";
import { useConfigQuery } from "services/staticDataService";
import * as submissionsService from "services/submissionsService";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const getStatusRowKey = (_row, index) => String(index);

// Internal name of the row-type control column. Declared as a DISPLAY_CONTROL
// column by the backend when grouping is active, but a summary row carries this
// value even on an ungrouped table — see displayControlColumn below.
const GROUP_CONTROL_COLUMN = "<[[Group Control]]>";

const dataGridContainerStaticSx = {
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  position: "relative",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  transition: "filter 0.2s ease-out, opacity 0.2s ease-out",
  "& .MuiDataGrid-root": {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    width: "100%",
    height: "100%",
  },

  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "grey.100",
    fontWeight: "bold",
  },
  "& .MuiDataGrid-cell": {
    borderColor: "divider",
  },
  "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered": {
    backgroundColor: "action.hover",
  },
  "& .MuiDataGrid-row.row-display-control-detail": {
    backgroundColor: "grey.100",
  },
  "& .MuiDataGrid-row.row-display-control-detail:hover, & .MuiDataGrid-row.row-display-control-detail.Mui-hovered": {
    backgroundColor: "grey.200",
  },
  "& .MuiDataGrid-row.row-display-control-grand-total": {
    backgroundColor: "success.main",
    color: "common.white",
    fontWeight: "bold",
  },
  "& .MuiDataGrid-row.row-display-control-grand-total:hover, & .MuiDataGrid-row.row-display-control-grand-total.Mui-hovered": {
    backgroundColor: "success.dark",
  },
  "& .MuiDataGrid-row.row-display-control-grand-total .MuiDataGrid-cell": {
    color: "inherit",
  },
  "& .MuiDataGrid-row.row-display-control-sub-total": {
    backgroundColor: "#2aae65",
    color: "common.black",
    fontWeight: "bold",
  },
  "& .MuiDataGrid-row.row-display-control-sub-total:hover, & .MuiDataGrid-row.row-display-control-sub-total.Mui-hovered": {
    backgroundColor: "#239957",
  },
  "& .MuiDataGrid-row.row-display-control-sub-total .MuiDataGrid-cell": {
    color: "inherit",
  },
  "& .MuiDataGrid-row.row-display-control-summary": {
    backgroundColor: "#8ed9b0",
    color: "common.black",
    fontWeight: "bold",
  },
  "& .MuiDataGrid-row.row-display-control-summary:hover, & .MuiDataGrid-row.row-display-control-summary.Mui-hovered": {
    backgroundColor: "#7ccfa1",
  },
  "& .MuiDataGrid-row.row-display-control-summary .MuiDataGrid-cell": {
    color: "inherit",
  },
  "& .MuiDataGrid-row.row-display-control-group": {
    fontWeight: "bold",
  },
  "& .status-edit-controls": {
    display: "none",
  },
  "& .status-cell:hover .status-edit-controls, & .status-cell.status-cell-active .status-edit-controls": {
    display: "flex",
  },
  "& .status-display-value": {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
  "& .status-cell:hover .status-display-value, & .status-cell.status-cell-active .status-display-value": {
    display: "none",
  },
  [`& .MuiDataGrid-cell.${CELL_ALIGN_CLASS_PREFIX}left`]: {
    justifyContent: "flex-start",
    textAlign: "left",
  },
  [`& .MuiDataGrid-cell.${CELL_ALIGN_CLASS_PREFIX}right`]: {
    justifyContent: "flex-end",
    textAlign: "right",
  },
  [`& .MuiDataGrid-cell.${CELL_ALIGN_CLASS_PREFIX}center`]: {
    justifyContent: "center",
    textAlign: "center",
  },
  "& .MuiDataGrid-cell.numeric-text-value": {
    justifyContent: "flex-start",
    textAlign: "left",
  },
  "& .MuiDataGrid-row.row-display-control-grand-total .MuiDataGrid-cell.numeric-text-value, & .MuiDataGrid-row.row-display-control-sub-total .MuiDataGrid-cell.numeric-text-value, & .MuiDataGrid-row.row-display-control-summary .MuiDataGrid-cell.numeric-text-value": {
    justifyContent: "flex-start",
    textAlign: "left",
  },
};

const RequestIndicator = ({ state }) => {
  // Only surface in-progress and failure states. On success we show nothing —
  // the status badge already reflects the new value, so an extra green tick is
  // redundant and looks odd lingering next to the status.
  if (!state || state === "success") {
    return <React.Fragment />;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      <Tooltip
        title={
          state === "error"
            ? "Failed to save changes to Marmalade"
            : "Uploading changes to Marmalade"
        }
      >
        {state === "error" ? (
          <FailedIcon style={{ color: "crimson" }} />
        ) : (
          <div>
            <DefaultLoader
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              size={14}
            />
          </div>
        )}
      </Tooltip>
    </Box>
  );
};

const ReportResults = ({
  reportData,
  allResults,
  selectedResultIndex = 0,
  onResultSelect,
  columnsData,
  reorderColumns,
  applyColumnRenames,
  savedReport,
  onResultsUpdate,
  rerun,
  isRerunning: isRerunningProp,
  isSummaryLoading,
  summaryResults,
  onRequestSummary,
  executionError,
  isStatusEditable = true,
  autoRun = false,
  fillWidth = false,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const describeDynamicDate = useCallback(
    (name) =>
      getDynamicDateDisplayName(
        reportsV3DynamicDatePresets(),
        name,
        translateLabels
      ),
    [translateLabels]
  );
  const aggregatorUtils = useGroupAggregators();
  const apiRef = useGridApiRef();
  if (apiRef.current === null) {
    apiRef.current = {};
  }
  const containerRef = useRef(null);
  const fullscreenContainerRef = useRef(null);
  const toolbarRef = useRef(null);
  const paginationRef = useRef(null);
  const metricsPanelRef = useRef(null);
  const sliderDraggingRef = useRef(false);
  const resizeRafRef = useRef(null);
  // Per-tab user pinning overrides: Map<tabIndex, {pinned: Set, unpinned: Set}>
  const userPinChangesRef = useRef(new Map());
  const { data: dateFormatConfig } = useConfigQuery("date_format");
  const dateFormat = dateFormatConfig?.date_format ?? null;

  useColumnPinning({
    apiRef,
    columnsDependency: columnsData,
  });
  const reportId = savedReport?.internalName;
  const reportName = savedReport?.displayName ?? "Report";

  useEffect(() => {
    userPinChangesRef.current = new Map();
  }, [reportId]);

  const isMultiTableReport = savedReport?.type === "MULTI_TABLE_REPORT";

  const currentReportSpec = useMemo(() => {
    if (!isMultiTableReport) return savedReport?.specification ?? null;
    const tableName = allResults?.[selectedResultIndex]?.tableName;
    if (!tableName) return null;
    return (
      (savedReport?.reports ?? []).find((r) => r.internalName === tableName)
        ?.specification ?? null
    );
  }, [isMultiTableReport, savedReport, allResults, selectedResultIndex]);

  const sourceTables = useMemo(
    () => currentReportSpec?.sourceTables || ["review_list"],
    [currentReportSpec?.sourceTables]
  );

  const { data: availableSources } = reportsV3Service.useGetTablesQuery();

  const {
    data: columnWidthsConfig,
  } = reportsV3Service.useGetReportConfigurationQuery(
    { reportId, configurationName: REPORT_CONFIGURATION_NAME },
    { skip: !reportId }
  );

  const { data: savedReports } = reportsV3Service.useGetSavedReportsQuery();
  const isReadOnly = useMemo(() => {
    if (!reportId || !savedReports) return false;
    const match = savedReports.find((r) => r.internalName === reportId);
    return match?.canWrite === false;
  }, [reportId, savedReports]);

  const isMultiSource = sourceTables.length > 1;

  const sourceDisplayNameMap = useMemo(
    () => buildSourceDisplayNameMap(availableSources),
    [availableSources]
  );

  const getColumnDataType = useCallback(
    (column) => {
      const columnDef = columnsData?.find((c) => c.column === column);
      return columnDef?._dataType;
    },
    [columnsData]
  );

  const {
    getDisplayValue: getEnumDisplayValue,
    getInputDisplayValue,
  } = useEnumDisplayValues(columnsData);

  const getEnumDisplayValueMapped = useCallback(
    (resultColumnName, value) => {
      if (value === null || value === undefined) return value;
      return getEnumDisplayValue(resultColumnName, value);
    },
    [getEnumDisplayValue]
  );

  const getInputEnumDisplayValueMapped = useCallback(
    (resultColumnName, value) => {
      if (value === null || value === undefined) return value;
      return getInputDisplayValue(resultColumnName, value);
    },
    [getInputDisplayValue]
  );
  const columnTypeMap = useMemo(() => buildColumnTypeMap(columnsData), [
    columnsData,
  ]);

  const [columnsSearchQuery, setColumnsSearchQuery] = useState("");

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const searchDebounceRef = useRef(null);

  const [, _setForceUpdate] = useState(0);
  const forceHeaderRendererUpdate = useCallback(
    () => _setForceUpdate((n) => n + 1),
    []
  );

  const {
    viewMode,
    metricsCollapsed,
    setMetricsCollapsed,
    expanded,
    handleViewModeChange,
    handleToggleMetricsCollapsed,
  } = useViewMode({
    reportName,
    fullscreenContainerRef,
  });
  const {
    filterMaps,
    dateInputUtils,
    sortModel,
    setSortModel,
    clearFilter,
    savedSortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    savedColumnVisibility,
    columnOrder,
    setColumnOrder,
    savedColumnOrder,
    pinnedColumns,
    setPinnedColumns,
    columnWidths,
    setColumnWidths,
    savedColumnWidths,
    columnDecimals,
    setColumnDecimals,
    searchQuery,
    setSearchQuery,
    columnNameOverrides,
    setColumnNameOverrides,
    groupByColumns,
    setGroupByColumns,
    savedGroupByColumns,
    columnAggregations,
    setColumnAggregations,
    extraAggregateColumns,
    setExtraAggregateColumns,
    removedExtraAggregateColumns,
    setRemovedExtraAggregateColumns,
    includeDetails,
    setIncludeDetails,
    includeTotal,
    setIncludeTotal,
    includeSubTotal,
    setIncludeSubTotal,
    applyConfigurationPayload,
    metricsItems,
    setMetricsItems,
    savedFilterMaps,
    activeFilters,
    activeInputFilters,
    inputFilterMaps,
    savedInputFilterMaps,
    tabStatesRef,
    pendingTrackedChanges,
    markAsSaved,
    savedMetricsItems,
    getActiveFilterGroups,
    helpers,
    hasUnsavedChanges,
    dirtySheets,
    allValuesEnumerations,
    setAllValuesEnumerations,
    summarySelections = {},
    setSummarySelections,
    summaryType = "BOTTOM",
    setSummaryType,
    rangeGroupings,
    savedRangeGroupings,
  } = useReportStateContext();

  const onSetColumnDecimals = useCallback(
    (column, valueOrNull) => {
      setColumnDecimals((prev) => {
        const next = { ...prev };
        if (valueOrNull === null || valueOrNull === undefined) {
          delete next[column];
        } else {
          next[column] = valueOrNull;
        }
        return next;
      });
    },
    [setColumnDecimals]
  );

  const handleToggleAllValuesEnumeration = useCallback(
    (columnName) => {
      setAllValuesEnumerations((prev) => {
        const next = new Set(prev);
        if (next.has(columnName)) {
          next.delete(columnName);
        } else {
          next.add(columnName);
        }
        return next;
      });
    },
    [setAllValuesEnumerations]
  );

  const showGroupHeader = groupByColumns.size > 0;

  const displayControlColumn = useMemo(() => {
    const declared = columnsData?.find((c) => c._type === "DISPLAY_CONTROL")
      ?.column;
    if (declared) return declared;
    // Grouping declares a DISPLAY_CONTROL column, but a summary row can be added
    // to any table and carries the control value without that column existing.
    // Fall back to the known control field so the row is still styled.
    const hasControlValue = (reportData?.tableContents ?? []).some(
      (row) => row[GROUP_CONTROL_COLUMN] != null
    );
    return hasControlValue ? GROUP_CONTROL_COLUMN : null;
  }, [columnsData, reportData]);

  const hideDetailStorageKey = reportId
    ? `reportsV3.hideDetail.${reportId}`
    : null;
  const [hideDetail, setHideDetail] = useState(() => {
    if (!hideDetailStorageKey) return false;
    try {
      return localStorage.getItem(hideDetailStorageKey) === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!hideDetailStorageKey) return;
    try {
      if (hideDetail) {
        localStorage.setItem(hideDetailStorageKey, "true");
      } else {
        localStorage.removeItem(hideDetailStorageKey);
      }
    } catch {}
  }, [hideDetail, hideDetailStorageKey]);

  const [addColDialog, setAddColDialog] = useState({
    open: false,
    sourceColumn: null,
    func: null,
    funcLabel: null,
  });
  const handleGroupByColumn = useCallback(
    (column) => {
      setGroupByColumns((prev) => new Set([...prev, column]));
      if (columnTypeMap[column]?.isEnum) {
        setAllValuesEnumerations((prev) => new Set([...prev, column]));
      }
    },
    [setGroupByColumns, setAllValuesEnumerations, columnTypeMap]
  );

  const handleUngroupColumn = useCallback(
    (column) => {
      setGroupByColumns((prev) => {
        const next = new Set(prev);
        next.delete(column);
        return next;
      });
      if (groupByColumns.size === 1 && groupByColumns.has(column)) {
        const extraColumnNames = new Set(
          extraAggregateColumns.map((e) => e.column)
        );
        setRemovedExtraAggregateColumns(
          (prev) => new Set([...prev, ...extraColumnNames])
        );
        setExtraAggregateColumns([]);
        setColumnOrder((prev) =>
          prev.filter((col) => !extraColumnNames.has(col))
        );
        setSortModel((prev) =>
          prev.filter((s) => !extraColumnNames.has(s.field))
        );
        extraColumnNames.forEach((name) => clearFilter(name));
      }
    },
    [
      setGroupByColumns,
      setExtraAggregateColumns,
      setRemovedExtraAggregateColumns,
      setColumnOrder,
      setSortModel,
      clearFilter,
      groupByColumns,
      extraAggregateColumns,
    ]
  );

  const handleAggregationChange = useCallback(
    (columnName, method) =>
      setColumnAggregations((prev) => ({ ...prev, [columnName]: method })),
    [setColumnAggregations]
  );

  // Per-column summary-row selections (summarySelections) and report-level
  // placement (summaryType: "TOP" | "BOTTOM" | "OFF") live in the tab state
  // from useReportState so they feed change detection and the report JSON.

  // Changing a column's summary value while the summary is switched off
  // re-enables it at the bottom.
  const enableSummaryIfOff = useCallback(() => {
    setSummaryType((prev) => (prev === "OFF" ? "BOTTOM" : prev));
  }, [setSummaryType]);

  const handleSetSummaryAggregator = useCallback(
    (columnName, func) => {
      setSummarySelections((prev) => ({ ...prev, [columnName]: { func } }));
      enableSummaryIfOff();
    },
    [setSummarySelections, enableSummaryIfOff]
  );

  const handleSetSummaryOverride = useCallback(
    (columnName, override) => {
      setSummarySelections((prev) => ({ ...prev, [columnName]: { override } }));
      enableSummaryIfOff();
    },
    [setSummarySelections, enableSummaryIfOff]
  );

  const handleClearSummary = useCallback(
    (columnName) => {
      setSummarySelections((prev) => {
        if (!(columnName in prev)) return prev;
        const next = { ...prev };
        delete next[columnName];
        return next;
      });
    },
    [setSummarySelections]
  );

  const hasSummarySelections = Object.keys(summarySelections).length > 0;
  // The summary header row is hidden when switched off, even if values exist.
  const showSummaryHeader = hasSummarySelections && summaryType !== "OFF";

  const handleOpenAddColDialog = useCallback(
    (sourceColumn, func, funcLabel) => {
      setAddColDialog({ open: true, sourceColumn, func, funcLabel });
    },
    []
  );

  const handleDeleteExtraCol = useCallback(
    (column) => {
      setExtraAggregateColumns((prev) =>
        prev.filter((e) => e.column !== column)
      );
      setRemovedExtraAggregateColumns((prev) => new Set([...prev, column]));
      setColumnOrder((prev) => prev.filter((col) => col !== column));
      setSortModel((prev) => prev.filter((s) => s.field !== column));
      clearFilter(column);
    },
    [
      setExtraAggregateColumns,
      setRemovedExtraAggregateColumns,
      setColumnOrder,
      setSortModel,
      clearFilter,
    ]
  );

  const existingColumnNames = useMemo(
    () =>
      collectExistingColumnNames(
        columnsData,
        columnNameOverrides,
        extraAggregateColumns
      ),
    [columnsData, columnNameOverrides, extraAggregateColumns]
  );

  const handleConfirmAddCol = useCallback(
    (name) => {
      const { sourceColumn, func } = addColDialog;
      setExtraAggregateColumns((prev) => [
        ...prev,
        { column: name, func, sourceColumn },
      ]);
      setRemovedExtraAggregateColumns((prev) => {
        if (!prev.has(name)) return prev;
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      setColumnOrder((prev) => {
        const sourceIdx = prev.indexOf(sourceColumn);
        const insertAt = sourceIdx >= 0 ? sourceIdx + 1 : prev.length;
        const next = [...prev];
        next.splice(insertAt, 0, name);
        return next;
      });
      setAddColDialog({
        open: false,
        sourceColumn: null,
        func: null,
        funcLabel: null,
      });
    },
    [
      addColDialog,
      setExtraAggregateColumns,
      setRemovedExtraAggregateColumns,
      setColumnOrder,
    ]
  );

  const isTypingRef = useRef(false);

  const handleSearchChange = useCallback(
    (value) => {
      isTypingRef.current = true;
      setSearchQuery(value);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        isTypingRef.current = false;
        setDebouncedSearchQuery(value.trim());
      }, 400);
    },
    [setSearchQuery]
  );

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isTypingRef.current) {
      setDebouncedSearchQuery(searchQuery.trim());
    }
  }, [searchQuery]);

  const hasLoadedWidthsConfig = useRef(false);
  useEffect(() => {
    if (!columnWidthsConfig || hasLoadedWidthsConfig.current) return;
    hasLoadedWidthsConfig.current = true;
    applyConfigurationPayload(columnWidthsConfig, savedReport);
  }, [columnWidthsConfig, savedReport, applyConfigurationPayload]);

  const truncatedColumnsRef = useRef(new Set());

  const clonedSeparatorsRef = useRef(new Map());

  const columnTypesCacheRef = useRef({});
  useEffect(() => {
    (columnsData ?? []).forEach((col) => {
      if (!columnTypesCacheRef.current[col.column]) {
        columnTypesCacheRef.current[col.column] = {
          _type: col._type ?? null,
          _subType: col._subType ?? null,
        };
      }
    });
  }, [columnsData]);

  const [patchSubmission] = submissionsService.usePatchSubmissionMutation();
  const [
    retrieveSubmission,
  ] = submissionsService.useLazyRetrieveSubmissionQuery();
  const [statusOverrides, setStatusOverrides] = useState({});
  const [savingStatusRows, setSavingStatusRows] = useState({});
  const [statusRequestStates, setStatusRequestStates] = useState({});
  const [activeStatusEditorRowId, setActiveStatusEditorRowId] = useState(null);
  const statusRequestTimeoutsRef = useRef({});

  useEffect(() => {
    const timeouts = statusRequestTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  const handleColumnReorder = useCallback(
    (newOrder) => {
      setColumnOrder(newOrder);
      reorderColumns?.(newOrder);
    },
    [setColumnOrder, reorderColumns]
  );

  // Drag-to-reorder from the Columns menu: move draggedId to targetId's slot
  // (header drag-reorder uses the same handleColumnReorder path).
  const handleReorderColumnDrop = useCallback(
    (draggedId, targetId) => {
      if (!draggedId || draggedId === targetId) return;
      const from = columnOrder.indexOf(draggedId);
      const to = columnOrder.indexOf(targetId);
      if (from < 0 || to < 0) return;
      const next = [...columnOrder];
      next.splice(from, 1);
      next.splice(to, 0, draggedId);
      handleColumnReorder(next);
    },
    [columnOrder, handleColumnReorder]
  );

  const {
    handlePinColumn: _handlePinColumn,
    handleUnpinColumn: _handleUnpinColumn,
    handleHideColumn,
  } = useColumnManagement({
    columnsData,
    reorderColumns: handleColumnReorder,
    setColumnVisibilityModel,
    pinnedColumns,
    setPinnedColumns,
  });

  const handlePinColumn = useCallback(
    (column) => {
      const changes = userPinChangesRef.current.get(selectedResultIndex) ?? {
        pinned: new Set(),
        unpinned: new Set(),
      };
      changes.pinned.add(column);
      changes.unpinned.delete(column);
      userPinChangesRef.current.set(selectedResultIndex, changes);
      _handlePinColumn(column);
    },
    [_handlePinColumn, selectedResultIndex]
  );

  const handleUnpinColumn = useCallback(
    (column) => {
      const changes = userPinChangesRef.current.get(selectedResultIndex) ?? {
        pinned: new Set(),
        unpinned: new Set(),
      };
      changes.unpinned.add(column);
      changes.pinned.delete(column);
      userPinChangesRef.current.set(selectedResultIndex, changes);
      _handleUnpinColumn(column);
    },
    [_handleUnpinColumn, selectedResultIndex]
  );

  const metricsColumnMethodMap = useMemo(() => {
    const map = {};
    for (const m of metricsItems ?? []) {
      if (!map[m.key]) map[m.key] = new Set();
      map[m.key].add(m.method);
    }
    return map;
  }, [metricsItems]);

  const handleAddToMetrics = useCallback(
    (columnName, methodOverride) => {
      const colDef = columnsData?.find((c) => c.column === columnName);
      const newMetric = buildMetricItem(columnName, colDef, methodOverride);
      setMetricsItems((prev) => [...prev, newMetric]);
    },
    [columnsData, setMetricsItems]
  );

  const handleRemoveFromMetrics = useCallback(
    (columnName, method) => {
      setMetricsItems((prev) =>
        method
          ? prev.filter((m) => !(m.key === columnName && m.method === method))
          : prev.filter((m) => m.key !== columnName)
      );
    },
    [setMetricsItems]
  );

  const orderedGridColumnsRef = useRef([]);
  const columnVisibilityModelRef = useRef({});

  const resolvedFilterMaps = useMemo(
    () =>
      filterMaps ?? {
        value: {},
        date: {},
        number: {},
      },
    [filterMaps]
  );
  const columnFilters = useMemo(() => resolvedFilterMaps.value ?? {}, [
    resolvedFilterMaps,
  ]);
  const dateColumnFilters = useMemo(() => resolvedFilterMaps.date ?? {}, [
    resolvedFilterMaps,
  ]);
  const numberColumnFilters = useMemo(() => resolvedFilterMaps.number ?? {}, [
    resolvedFilterMaps,
  ]);
  const resolvedSavedFilterMaps = useMemo(
    () => savedFilterMaps ?? { value: {}, date: {}, number: {} },
    [savedFilterMaps]
  );
  const savedColumnFilters = useMemo(
    () => resolvedSavedFilterMaps.value ?? {},
    [resolvedSavedFilterMaps]
  );
  const savedDateColumnFilters = useMemo(
    () => resolvedSavedFilterMaps.date ?? {},
    [resolvedSavedFilterMaps]
  );
  const savedNumberColumnFilters = useMemo(
    () => resolvedSavedFilterMaps.number ?? {},
    [resolvedSavedFilterMaps]
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterAnchorOverride, setFilterAnchorOverride] = useState(null);
  const [filterOpenedFromColumnMenu, setFilterOpenedFromColumnMenu] = useState(
    false
  );
  const rerunAfterRenameRemoveRef = useRef(null);
  const rerunAfterSaveRef = useRef(null);

  const {
    columnMenuAnchor,
    menuColumn,
    renameDialogOpen,
    renameField,
    renameValue,
    columnMenuOpen,
    handleColumnHeaderClick,
    handleColumnHeaderKeyDown,
    handleColumnHeaderKeyUp,
    handleColumnHeaderContextMenu,
    handleCloseColumnMenu,
    handleSortAsc,
    handleSortDesc,
    handleClearSort,
    isMenuColumnSorted,
    handleRenameColumn,
    handleRenameConfirm,
    handleRenameCancel,
    handleRenameRemove,
    setRenameValue,
  } = useColumnMenu({
    apiRef,
    clonedSeparatorsRef,
    truncatedColumnsRef,
    getOrderedGridColumns: useCallback(() => orderedGridColumnsRef.current, []),
    getColumnVisibilityModel: useCallback(
      () => columnVisibilityModelRef.current,
      []
    ),
    setColumnNameOverrides,
    onRenameRemove: useCallback(() => {
      rerunAfterRenameRemoveRef.current?.();
    }, []),
  });

  const rows = useMemo(
    () =>
      buildGridRows(
        reportData?.tableContents,
        statusOverrides,
        hideDetail,
        displayControlColumn
      ),
    [reportData, statusOverrides, hideDetail, displayControlColumn]
  );

  const hasDetailRows = useMemo(
    () => checkHasDetailRows(reportData?.tableContents, displayControlColumn),
    [reportData, displayControlColumn]
  );
  const columnDefs = useMemo(() => {
    const cols = reportData?.columns ?? [];
    return cols.map((col) => ({
      internalName: col.internalName,
      displayName: col.displayName,
      dataType: "text",
      displayType: "normal",
    }));
  }, [reportData]);

  const columnDefMap = useMemo(() => {
    const map = {};
    columnsData?.forEach((col) => {
      map[col.column] = col;
    });
    return map;
  }, [columnsData]);

  const metadataDisplayNameMap = useMemo(
    () => buildMetadataDisplayNameMap(columnsData),
    [columnsData]
  );

  const getColumnDisplayName = useCallback(
    (columnName) =>
      resolveColumnDisplayName(
        columnName,
        columnNameOverrides,
        columnDefMap,
        metadataDisplayNameMap
      ),
    [columnNameOverrides, columnDefMap, metadataDisplayNameMap]
  );

  const columnDisplayNameMap = useMemo(
    () =>
      buildColumnDisplayNameMap(
        columnDefs,
        columnNameOverrides,
        metadataDisplayNameMap
      ),
    [columnDefs, columnNameOverrides, metadataDisplayNameMap]
  );

  const notForExportColumns = useMemo(() => {
    return new Set(
      (columnsData ?? []).filter((c) => c.notForExport).map((c) => c.column)
    );
  }, [columnsData]);

  const sourceColumnsForDisplay = useMemo(() => {
    return (columnsData ?? [])
      .filter((c) => !isGroupingColumn(c.column))
      .map((c) => ({
        internalName: c.column,
        displayName: c.displayName,
      }));
  }, [columnsData]);

  const savedSourceColumns = useMemo(
    () => currentReportSpec?.sourceColumns ?? [],
    [currentReportSpec?.sourceColumns]
  );

  const rangeGroupingAvailableColumns = useMemo(
    () => [
      ...(columnsData ?? []).map((c) => c.column),
      ...savedSourceColumns.map((sc) => sc.column),
    ],
    [columnsData, savedSourceColumns]
  );

  const getRangeGroupingColumnType = useCallback(
    (column) => columnsData?.find((c) => c.column === column)?._type ?? null,
    [columnsData]
  );

  const getRangeGroupingColumnSubType = useCallback(
    (column) => columnsData?.find((c) => c.column === column)?._subType ?? null,
    [columnsData]
  );

  const {
    activeRangeGroupings,
    isRangeGrouped,
    dialogProps: rangeGroupingDialogProps,
    openRangeGrouping,
    editRangeGrouping,
    ungroupRangeGrouping,
  } = useRangeGrouping({
    availableColumns: rangeGroupingAvailableColumns,
    getColumnDisplayName,
    getColumnType: getRangeGroupingColumnType,
    getColumnSubType: getRangeGroupingColumnSubType,
    onUngroupColumn: handleUngroupColumn,
  });

  const rangeGroupedColumns = useMemo(
    () => new Set(Object.keys(activeRangeGroupings)),
    [activeRangeGroupings]
  );

  const filteredColumns = useMemo(
    () => buildFilteredColumnSet(filterMaps, inputFilterMaps),
    [filterMaps, inputFilterMaps]
  );

  const headerRenderer = useMemo(
    () =>
      createHeaderRenderer({
        columnFilters,
        dateColumnFilters,
        numberColumnFilters,
        sortModel,
        truncatedColumnsRef,
        menuColumn,
        forceUpdate: forceHeaderRendererUpdate,
        onHeaderContextMenu: handleColumnHeaderContextMenu,
        showFilterIndicator: true,
        groupByColumns,
        showGroupHeader,
        columnTypeMap,
        columnAggregations,
        onAggregationChange: handleAggregationChange,
        extraAggregateColumns,
        onAddAggregateColumn: handleOpenAddColDialog,
        onDeleteExtraColumn: handleDeleteExtraCol,
        aggregatorUtils,
        allValuesEnumerations,
        onToggleAllValuesEnumeration: handleToggleAllValuesEnumeration,
        summarySelections,
        showSummaryHeader,
        rangeGroupedColumns,
        filteredColumns,
        t: translateLabels,
      }),
    [
      columnFilters,
      dateColumnFilters,
      numberColumnFilters,
      sortModel,
      menuColumn,
      forceHeaderRendererUpdate,
      handleColumnHeaderContextMenu,
      groupByColumns,
      showGroupHeader,
      columnTypeMap,
      columnAggregations,
      handleAggregationChange,
      extraAggregateColumns,
      handleOpenAddColDialog,
      handleDeleteExtraCol,
      aggregatorUtils,
      allValuesEnumerations,
      handleToggleAllValuesEnumeration,
      summarySelections,
      showSummaryHeader,
      rangeGroupedColumns,
      filteredColumns,
      translateLabels,
    ]
  );
  const pinnedHeaderRenderer = useMemo(
    () =>
      createHeaderRenderer({
        columnFilters,
        dateColumnFilters,
        numberColumnFilters,
        sortModel,
        truncatedColumnsRef,
        menuColumn,
        forceUpdate: forceHeaderRendererUpdate,
        onHeaderContextMenu: handleColumnHeaderContextMenu,
        showFilterIndicator: true,
        startAdornment: () => (
          <PushPinIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />
        ),
        groupByColumns,
        showGroupHeader,
        columnTypeMap,
        columnAggregations,
        onAggregationChange: handleAggregationChange,
        extraAggregateColumns,
        onAddAggregateColumn: handleOpenAddColDialog,
        onDeleteExtraColumn: handleDeleteExtraCol,
        aggregatorUtils,
        allValuesEnumerations,
        onToggleAllValuesEnumeration: handleToggleAllValuesEnumeration,
        summarySelections,
        showSummaryHeader,
        rangeGroupedColumns,
        filteredColumns,
        t: translateLabels,
      }),
    [
      columnFilters,
      dateColumnFilters,
      numberColumnFilters,
      sortModel,
      menuColumn,
      forceHeaderRendererUpdate,
      handleColumnHeaderContextMenu,
      groupByColumns,
      showGroupHeader,
      columnTypeMap,
      columnAggregations,
      handleAggregationChange,
      extraAggregateColumns,
      handleOpenAddColDialog,
      handleDeleteExtraCol,
      aggregatorUtils,
      allValuesEnumerations,
      handleToggleAllValuesEnumeration,
      summarySelections,
      showSummaryHeader,
      rangeGroupedColumns,
      filteredColumns,
      translateLabels,
    ]
  );

  const buildWorkingSpecification = useCallback(() => {
    const savedOutputColMap = new Map(
      (currentReportSpec?.outputColumns ?? []).map((oc) => [oc.column, oc])
    );
    const savedSourceColumnSet = new Set(
      savedSourceColumns.map((sc) => sc.column).filter(Boolean)
    );
    const specColumnsData = (columnsData ?? []).filter(
      (c) =>
        savedOutputColMap.has(c.column) || savedSourceColumnSet.has(c.column)
    );
    const currentColumnSet = new Set(specColumnsData.map((c) => c.column));
    const hiddenSourceCols = [...savedSourceColumnSet]
      .filter((col) => !currentColumnSet.has(col))
      .map((col) => {
        const cached = columnTypesCacheRef.current[col] ?? {};
        return {
          column: col,
          _type: cached._type ?? null,
          _subType: cached._subType ?? null,
        };
      });
    const columnsForGroupSpec = [...specColumnsData, ...hiddenSourceCols];

    const tabState = {
      pinnedColumns,
      columnOrder,
      columnVisibilityModel,
      columnNameOverrides,
      sortModel,
      filters: activeFilters,
      preAggregationFilters: activeInputFilters,
      groupByColumns,
      columnAggregations,
      extraAggregateColumns,
      removedExtraAggregateColumns,
      includeDetails,
      includeTotal,
      includeSubTotal,
      allValuesEnumerations,
      summarySelections,
      summaryType,
      columnDecimals,
      rangeGroupings,
    };

    const result = buildSpecFromTabState(currentReportSpec, tabState, {
      notForExportColumns,
      metadataDisplayNames: metadataDisplayNameMap,
      columnsForGrouping: columnsForGroupSpec,
      searchQuery: debouncedSearchQuery,
    });

    // Keep every metric's key column in the executed query (hidden if not
    // displayed), so subsetting/removing columns can't break the metric cards.
    return ensureMetricColumns(result, metricsItems);
  }, [
    currentReportSpec,
    savedSourceColumns,
    columnsData,
    pinnedColumns,
    columnOrder,
    columnVisibilityModel,
    columnNameOverrides,
    sortModel,
    activeFilters,
    activeInputFilters,
    groupByColumns,
    columnAggregations,
    extraAggregateColumns,
    removedExtraAggregateColumns,
    includeDetails,
    includeTotal,
    includeSubTotal,
    allValuesEnumerations,
    summarySelections,
    summaryType,
    columnDecimals,
    rangeGroupings,
    notForExportColumns,
    metadataDisplayNameMap,
    debouncedSearchQuery,
    metricsItems,
  ]);

  const buildMultiTableReportData = useCallback(() => {
    const reportByName = new Map(
      (savedReport.reports ?? []).map((r) => [r.internalName, r])
    );

    const notForOutputReports = (savedReport.reports ?? []).filter(
      (r) => r.notForOutput
    );

    const outputReports = (allResults ?? [])
      .map((result, idx) => {
        const subReport = reportByName.get(result.tableName);
        if (!subReport) return null;

        const base = {
          ...subReport,
          displayName: result.tableDisplayName ?? subReport.displayName,
        };

        if (idx === selectedResultIndex) {
          return {
            ...base,
            specification: buildRequestSpec(buildWorkingSpecification),
          };
        }

        const sheetState = tabStatesRef.current.get(idx);
        if (!sheetState) return base;

        return {
          ...base,
          specification: buildRequestSpec(() =>
            buildSpecFromTabState(subReport.specification, sheetState)
          ),
        };
      })
      .filter(Boolean);

    return {
      type: "MULTI_TABLE_REPORT",
      displayName: savedReport.displayName ?? "",
      description: savedReport.description ?? "",
      reports: [...notForOutputReports, ...outputReports],
    };
  }, [
    savedReport,
    allResults,
    selectedResultIndex,
    buildWorkingSpecification,
    tabStatesRef,
  ]);

  const buildReportData = useCallback(() => {
    if (isMultiTableReport) {
      return buildMultiTableReportData();
    }
    return {
      type: savedReport?.type ?? "SINGLE_TABLE_REPORT",
      displayName: savedReport?.displayName ?? "",
      description: savedReport?.description ?? "",
      specification: buildRequestSpec(buildWorkingSpecification),
    };
  }, [
    isMultiTableReport,
    buildMultiTableReportData,
    savedReport,
    buildWorkingSpecification,
  ]);
  const {
    downloadLoading,
    downloadError,
    handleDownloadXLSX,
  } = useXlsxDownload({
    reportName,
    buildReportData,
  });

  const handleSaveSuccess = useCallback(() => {
    const needsRerun = pendingTrackedChanges?.some((c) =>
      [
        "groupBy",
        "columnFilter",
        "dateFilter",
        "numberFilter",
        INPUT_COLUMN_FILTER_CHANGE_TYPE,
        INPUT_DATE_FILTER_CHANGE_TYPE,
        INPUT_NUMBER_FILTER_CHANGE_TYPE,
        "sortOrder",
      ].includes(c.type)
    );
    applyColumnRenames?.(columnNameOverrides);
    markAsSaved(buildWorkingSpecification());
    if (needsRerun) {
      rerunAfterSaveRef.current?.();
    }
  }, [
    applyColumnRenames,
    buildWorkingSpecification,
    columnNameOverrides,
    markAsSaved,
    pendingTrackedChanges,
  ]);

  const buildConfigurationPayload = useCallback(
    () => buildConfigurationPayloadUtil(savedReport, tabStatesRef.current),
    [savedReport, tabStatesRef]
  );

  const dispatch = useDispatch();
  // Review-tab-only: the dashboard report is read-only, so instead of a
  // server-side save we let users persist their layout to the browser.
  const isLocalLayoutReport = reportId === DASHBOARD_REPORT_ID;

  // Saved views live in the browser; bump this to re-read them after a change.
  const [layoutTick, setLayoutTick] = useState(0);
  const layoutsState = useMemo(() => {
    void layoutTick; // re-read localStorage whenever a saved view changes
    return loadLayoutNames(reportId);
  }, [reportId, layoutTick]);
  // Refetch the dashboard report so the newly-active layout (or default) is the
  // spec that executes (the spec change re-inits state — no remount).
  const refetchReport = useCallback(() => {
    dispatch(
      reportsV3Service.reportsV3Api.util.invalidateTags([
        { type: "SavedReports", id: reportId },
      ])
    );
  }, [dispatch, reportId]);

  // Save (or overwrite) the current view under a name and make it active.
  const handleSaveLayoutAs = useCallback(
    (name) => {
      const spec = buildWorkingSpecification();
      saveNamedLayout(reportId, name, {
        specification: spec,
        configuration: buildConfigurationPayload(),
      });
      applyColumnRenames?.(columnNameOverrides);
      markAsSaved(spec);
      setLayoutTick((t) => t + 1);
    },
    [
      buildWorkingSpecification,
      buildConfigurationPayload,
      reportId,
      applyColumnRenames,
      columnNameOverrides,
      markAsSaved,
    ]
  );

  // Switch to a saved view (name) or the company default (null) and re-execute.
  const handleSelectLayout = useCallback(
    (name) => {
      setActiveLayout(reportId, name);
      setLayoutTick((t) => t + 1);
      refetchReport();
    },
    [reportId, refetchReport]
  );

  const handleDeleteLayout = useCallback(
    (name) => {
      const wasActive = layoutsState.activeName === name;
      deleteNamedLayout(reportId, name);
      setLayoutTick((t) => t + 1);
      if (wasActive) refetchReport();
    },
    [reportId, layoutsState.activeName, refetchReport]
  );

  const {
    saveAsDialogOpen,
    saveAsName,
    setSaveAsName,
    saveAsDescription,
    setSaveAsDescription,
    saveAsError,
    clearSaveAsError,
    isCreatingReport,
    isSavingReport,
    handleSaveAllChanges,
    handleOpenSaveAsDialog,
    handleCloseSaveAsDialog,
    handleSaveAs,
  } = useSaveReport({
    savedReport,
    buildReportData,
    buildConfigurationPayload,
    onSaveSuccess: handleSaveSuccess,
  });

  const {
    registerExportHandler,
    setIsExporting,
    registerRunHandler,
    setIsRunning,
    asAt,
    isAsAtInvalid,
  } = useReportExportContext() ?? {};

  const groupingActive = groupByColumns.size > 0;
  const buildInputSummaryReport = useCallback(
    () => buildInputFilterSummaryReport(buildReportData(), selectedResultIndex),
    [buildReportData, selectedResultIndex]
  );
  const inputFilterSummary = useInputFilterSummary({
    buildSummaryReport: buildInputSummaryReport,
    asAt,
  });
  const {
    summary: inputSummary,
    loading: isInputSummaryLoading,
    failed: isInputSummaryFailed,
    settled: isInputSummarySettled,
    ensureLoaded: ensureInputSummaryLoaded,
  } = inputFilterSummary;
  const resolveSource = useCallback(
    (column, kind) =>
      resolveFilterSource({
        column,
        kind,
        groupingActive,
        rangeGroupedColumns,
        columnsData,
        summaryResults,
        inputSummary,
      }),
    [
      groupingActive,
      rangeGroupedColumns,
      columnsData,
      summaryResults,
      inputSummary,
    ]
  );
  const getAddFilterColumnFilterType = useCallback(
    (column, kind) => resolveSource(column, kind).filterType,
    [resolveSource]
  );
  const isAddFilterBinaryColumn = useCallback(
    (column, kind) => resolveSource(column, kind).isBinary,
    [resolveSource]
  );

  const {
    addFilterPanel,
    addFilterPanelSetters,
    hasAddFilterPendingChanges,
    applyFromPanel: handleApplyNewFilter,
    resetPanel: resetAddFilterPanel,
    targetColumn: addFilterTargetColumn,
  } = useAddFilterPanel({
    getColumnFilterType: getAddFilterColumnFilterType,
    isBinaryColumn: isAddFilterBinaryColumn,
    rangeGroupedColumns,
  });

  const {
    column: addFilterColumn,
    kind: addFilterKind,
    valueSearch: addFilterValueSearch,
    selectedValues: addFilterSelectedValues,
    dateRange: addFilterDateRange,
    numberRange: addFilterNumberRange,
  } = addFilterPanel;

  const {
    setColumn: setAddFilterColumn,
    setValueSearch: setAddFilterValueSearch,
    setSelectedValues: setAddFilterSelectedValues,
    setDateRange: setAddFilterDateRange,
    setNumberRange: setAddFilterNumberRange,
  } = addFilterPanelSetters;

  const handleFilterColumn = useCallback(
    (kind) => {
      if (menuColumn) {
        onRequestSummary?.();
        setAddFilterColumn(menuColumn, kind);
        setFilterAnchorOverride(columnMenuAnchor ?? null);
        setFilterOpenedFromColumnMenu(true);
        setFiltersOpen(true);
      }
      handleCloseColumnMenu();
    },
    [
      menuColumn,
      columnMenuAnchor,
      setAddFilterColumn,
      handleCloseColumnMenu,
      onRequestSummary,
    ]
  );

  const addFilterSource = useMemo(
    () => resolveSource(addFilterColumn, addFilterKind),
    [resolveSource, addFilterColumn, addFilterKind]
  );
  const addFilterUsesInputSummary = addFilterSource.usesInputSummary;
  const addFilterSourceFilterType = addFilterSource.filterType;
  useEffect(() => {
    if (
      addFilterUsesInputSummary &&
      addFilterColumn &&
      addFilterColumn !== FILTER_SELECT_COLUMN
    ) {
      ensureInputSummaryLoaded();
    }
  }, [
    addFilterUsesInputSummary,
    addFilterColumn,
    ensureInputSummaryLoaded,
    asAt,
    buildInputSummaryReport,
  ]);

  const getFilterKinds = useCallback(
    (column) =>
      filterKindsForColumn(column, {
        groupingActive,
        extraAggregateColumns,
        hasInputFilter: FILTER_MAP_KEYS.some((key) =>
          Object.prototype.hasOwnProperty.call(
            inputFilterMaps?.[key] ?? {},
            column
          )
        ),
      }),
    [groupingActive, extraAggregateColumns, inputFilterMaps]
  );
  const { handleExport } = useExportReport({
    savedReport,
    buildReportData,
    buildConfigurationPayload,
    onLoadingChange: setIsExporting,
  });
  useEffect(() => {
    return registerExportHandler?.(handleExport);
  }, [registerExportHandler, handleExport]);

  useLocalStorageSync({
    reportName,
    metricsCollapsed,
    setMetricsCollapsed,
  });

  const getChangeDetails = useCallback(
    (change) => {
      const savedState = [];
      const currentState = [];

      if (change.type === "columns") {
        savedState.push(...savedColumnOrder);
        currentState.push(...columnOrder);
        const columnOrderDiff = getColumnOrderDiff(
          savedColumnOrder,
          columnOrder
        );
        const getHiddenCols = (vis) =>
          Object.entries(vis)
            .filter(([, v]) => v === false)
            .map(([k]) => k);
        const savedHiddenSet = new Set(getHiddenCols(savedColumnVisibility));
        const currentHiddenSet = new Set(getHiddenCols(columnVisibilityModel));
        return {
          savedState,
          currentState,
          columnOrderDiff,
          savedHiddenSet,
          currentHiddenSet,
        };
      }

      if (change.type === "sortOrder") {
        return {
          savedState: formatSortItems(savedSortModel),
          currentState: formatSortItems(sortModel),
        };
      }

      if (change.type === "metrics") {
        return {
          savedState: formatMetricItems(savedMetricsItems),
          currentState: formatMetricItems(metricsItems),
        };
      }

      if (change.type === "columnWidths") {
        const extraColumns = new Set(
          extraAggregateColumns.map((extra) => extra.column)
        );
        return {
          savedState,
          currentState,
          widthChanges: describeColumnWidthChanges({
            savedWidths: savedColumnWidths,
            currentWidths: columnWidths,
            columnOrder,
            defaultWidth: (column) =>
              columnsData?.find((c) => c.column === column)?._width ??
              (extraColumns.has(column)
                ? DEFAULT_EXTRA_COLUMN_WIDTH_PX
                : DEFAULT_COLUMN_WIDTH_PX),
            displayName: getColumnDisplayName,
            texts: {
              increased: translateLabels(
                labels.CHANGE_DETAILS_WIDTH_INCREASED,
                { default: "width increased" }
              ),
              reduced: translateLabels(labels.CHANGE_DETAILS_WIDTH_REDUCED, {
                default: "width reduced",
              }),
              increasedSlightly: translateLabels(
                labels.CHANGE_DETAILS_WIDTH_INCREASED_SLIGHTLY,
                { default: "width increased slightly" }
              ),
              reducedSlightly: translateLabels(
                labels.CHANGE_DETAILS_WIDTH_REDUCED_SLIGHTLY,
                { default: "width reduced slightly" }
              ),
            },
          }),
        };
      }

      if (change.type === "groupBy") {
        const rangeOptions = {
          displayName: getColumnDisplayName,
          formatValue: (source, value) =>
            formatStoredThreshold(
              value,
              columnsData?.find((c) => c.column === source)?._subType ?? null,
              dateFormat || DEFAULT_DATE_FORMAT
            ),
          templates: {
            heading: translateLabels(labels.CHANGE_DETAILS_RANGE_HEADING, {
              default: "{name}:",
            }),
            lessThan: translateLabels(labels.CHANGE_DETAILS_RANGE_LESS_THAN, {
              default: "less than {value} -> {label}",
            }),
            matches: translateLabels(labels.CHANGE_DETAILS_RANGE_MATCHES, {
              default: "matches {value} -> {label}",
            }),
            otherwise: translateLabels(labels.CHANGE_DETAILS_RANGE_OTHERWISE, {
              default: "otherwise {label}",
            }),
          },
        };
        return {
          savedState: formatGroupByItems(
            savedGroupByColumns,
            getColumnDisplayName,
            (column) =>
              describeRangeGroupedColumn(
                column,
                savedRangeGroupings,
                rangeOptions
              )
          ),
          currentState: formatGroupByItems(
            groupByColumns,
            getColumnDisplayName,
            (column) =>
              describeRangeGroupedColumn(column, rangeGroupings, rangeOptions)
          ),
        };
      }

      const column = change.column;
      if (!column) {
        return { savedState, currentState };
      }
      const isInput = [
        INPUT_COLUMN_FILTER_CHANGE_TYPE,
        INPUT_DATE_FILTER_CHANGE_TYPE,
        INPUT_NUMBER_FILTER_CHANGE_TYPE,
      ].includes(change.type);
      const currentMaps = isInput
        ? inputFilterMaps ?? {}
        : {
            value: columnFilters,
            date: dateColumnFilters,
            number: numberColumnFilters,
          };
      const savedMaps = isInput
        ? savedInputFilterMaps ?? {}
        : {
            value: savedColumnFilters,
            date: savedDateColumnFilters,
            number: savedNumberColumnFilters,
          };
      if (
        change.type === "columnFilter" ||
        change.type === INPUT_COLUMN_FILTER_CHANGE_TYPE
      ) {
        const saved = Array.from(savedMaps.value?.[column] || []);
        const current = Array.from(currentMaps.value?.[column] || []);
        const toDisplay = isInput
          ? getInputEnumDisplayValueMapped
          : getEnumDisplayValueMapped;
        const mapToDisplay = (vals) =>
          vals.map((val) => toDisplay(column, val) ?? val);
        savedState.push(
          ...(saved.length ? mapToDisplay(saved) : ["No filter"])
        );
        currentState.push(
          ...(current.length ? mapToDisplay(current) : ["No filter"])
        );
      } else if (
        change.type === "dateFilter" ||
        change.type === INPUT_DATE_FILTER_CHANGE_TYPE
      ) {
        const saved = savedMaps.date?.[column];
        const current = currentMaps.date?.[column];
        const formatDate = (d) => (d ? d.toLocaleDateString() : "—");
        return formatRangeFilter(
          saved,
          current,
          formatDate,
          ["From", "To"],
          describeDynamicDate
        );
      } else if (
        change.type === "numberFilter" ||
        change.type === INPUT_NUMBER_FILTER_CHANGE_TYPE
      ) {
        const saved = savedMaps.number?.[column];
        const current = currentMaps.number?.[column];
        const fmt = columnFilterNumberFormatter(
          columnsData,
          column,
          filterKindForChangeType(change.type),
          getColumnDataType(column)
        );
        return formatRangeFilter(saved, current, (n) => fmt.display(n), [
          "Min",
          "Max",
        ]);
      }
      return { savedState, currentState };
    },
    [
      columnOrder,
      savedColumnOrder,
      sortModel,
      savedSortModel,
      columnVisibilityModel,
      savedColumnVisibility,
      columnFilters,
      savedColumnFilters,
      dateColumnFilters,
      savedDateColumnFilters,
      numberColumnFilters,
      savedNumberColumnFilters,
      inputFilterMaps,
      savedInputFilterMaps,
      getEnumDisplayValueMapped,
      getInputEnumDisplayValueMapped,
      getColumnDataType,
      columnsData,
      metricsItems,
      savedMetricsItems,
      groupByColumns,
      savedGroupByColumns,
      getColumnDisplayName,
      describeDynamicDate,
      columnWidths,
      savedColumnWidths,
      extraAggregateColumns,
      translateLabels,
      rangeGroupings,
      savedRangeGroupings,
      dateFormat,
    ]
  );

  // "Treat today as" date the *currently-viewed* result was computed for,
  // echoed back by the back-end onto the result object as `asAt` (sibling of
  // tableContents/columns), in the company date format. Reading it from
  // reportData means it tracks the active tab. Null when the viewed result has
  // no as-at (or an unparseable one) so we render nothing.
  const viewedAsAtLabel = useMemo(
    () => formatAsAtDate(reportData?.asAt, dateFormat),
    [reportData?.asAt, dateFormat]
  );

  const isRerunningReport = isRerunningProp ?? false;

  useEffect(() => {
    if (!reportData?.columns) return;
    const resultPinned = new Set(
      reportData.columns.filter((c) => c.pinned).map((c) => c.internalName)
    );
    const changes = userPinChangesRef.current.get(selectedResultIndex);
    if (changes) {
      changes.pinned.forEach((col) => resultPinned.add(col));
      changes.unpinned.forEach((col) => resultPinned.delete(col));
    }
    setPinnedColumns([...resultPinned]);
  }, [reportData, selectedResultIndex, setPinnedColumns]);

  const rerunReportWithCurrentState = useCallback(
    async (overrideSortModel) => {
      if (!rerun) return;
      // The as-at field holds text that is not a date; it shows how to fix
      // it, so do not run without the date the user meant.
      if (isAsAtInvalid) return;

      // Optional "as at" date lifted into ReportExportContext (set via the field
      // beside the Run button). Blank/none means no as-at is sent.
      const asAtArg = asAt || undefined;

      if (isMultiTableReport) {
        let reportData = buildMultiTableReportData();
        if (overrideSortModel) {
          let outputIdx = 0;
          reportData = {
            ...reportData,
            reports: reportData.reports.map((r) => {
              if (r.notForOutput) return r;
              const thisIdx = outputIdx++;
              if (thisIdx !== selectedResultIndex) return r;
              return {
                ...r,
                specification: {
                  ...r.specification,
                  order: overrideSortModel.map(({ field, sort }) => ({
                    column: field,
                    direction: (sort ?? "asc").toUpperCase(),
                  })),
                },
              };
            }),
          };
        }
        await rerun(reportData, asAtArg);
        return;
      }

      const spec = buildWorkingSpecification();
      if (overrideSortModel) {
        spec.order = rewriteOrderForRangeGroupings(
          overrideSortModel.map(({ field, sort }) => ({
            column: field,
            direction: sort?.toUpperCase() || "ASC",
          })),
          activeRangeGroupings
        );
      }
      await rerun(spec, asAtArg);
    },
    [
      rerun,
      asAt,
      isAsAtInvalid,
      isMultiTableReport,
      buildMultiTableReportData,
      selectedResultIndex,
      buildWorkingSpecification,
      activeRangeGroupings,
    ]
  );
  rerunAfterRenameRemoveRef.current = rerunReportWithCurrentState;
  rerunAfterSaveRef.current = rerunReportWithCurrentState;
  useEffect(() => {
    if (!registerRunHandler) return;
    return registerRunHandler(() => rerunReportWithCurrentState());
  }, [registerRunHandler, rerunReportWithCurrentState]);

  // Auto-apply mode (embedded dashboard report): there is no "Run" button, so
  // re-execute whenever any user-driven query input changes — filters, the
  // debounced search, sort, grouping, summary functions, and the include
  // detail/total toggles. Without this, those changes update the working state
  // but never re-query, so they appear to "do nothing". Fingerprint only the
  // inputs (not result-derived state) to avoid loops; Sets are spread to arrays
  // so they serialize. The ref starts at the initial state so the first load
  // isn't double-run.
  const autoRunFingerprintRef = useRef(undefined);
  useEffect(() => {
    if (!autoRun) return;
    const fingerprint = JSON.stringify({
      filters: (activeFilters ?? [])
        .filter((f) => f._status !== "reverted")
        .map((f) => f._spec),
      inputFilters: (activeInputFilters ?? []).map((f) => f._spec),
      search: debouncedSearchQuery ?? "",
      sort: sortModel ?? [],
      groupBy: [...(groupByColumns ?? [])],
      aggregations: columnAggregations ?? {},
      extraAggregates: extraAggregateColumns ?? [],
      includeDetails,
      includeTotal,
      includeSubTotal,
      summarySelections: summarySelections ?? {},
      summaryType,
      allValues: [...(allValuesEnumerations ?? [])],
      rangeGroupings: rangeGroupings ?? {},
    });
    if (autoRunFingerprintRef.current === undefined) {
      autoRunFingerprintRef.current = fingerprint;
      return;
    }
    if (fingerprint !== autoRunFingerprintRef.current) {
      autoRunFingerprintRef.current = fingerprint;
      void rerunReportWithCurrentState();
    }
  }, [
    autoRun,
    activeFilters,
    activeInputFilters,
    debouncedSearchQuery,
    sortModel,
    groupByColumns,
    columnAggregations,
    extraAggregateColumns,
    includeDetails,
    includeTotal,
    includeSubTotal,
    summarySelections,
    summaryType,
    allValuesEnumerations,
    rangeGroupings,
    rerunReportWithCurrentState,
  ]);
  useEffect(() => {
    setIsRunning?.(isRerunningReport);
  }, [setIsRunning, isRerunningReport]);

  useEffect(() => {
    setAddFilterValueSearch("");
    setAddFilterSelectedValues(new Set());
    setAddFilterDateRange({
      from: null,
      to: null,
    });
    setAddFilterNumberRange({
      min: null,
      max: null,
    });
    if (!addFilterColumn) return;

    const seedMaps =
      addFilterKind === FILTER_KIND_INPUT
        ? inputFilterMaps ?? {}
        : resolvedFilterMaps;
    const existingValueFilter = seedMaps.value?.[addFilterTargetColumn];
    const existingDateFilter = seedMaps.date?.[addFilterTargetColumn];
    const existingNumberFilter = seedMaps.number?.[addFilterTargetColumn];
    const columnFilterType = addFilterSourceFilterType;
    if (
      columnFilterType === "number" &&
      existingValueFilter?.has("") === true
    ) {
      setAddFilterNumberRange({ min: null, max: null, isEmpty: true });
    } else if (
      columnFilterType === "date" &&
      existingValueFilter?.has("") === true
    ) {
      setAddFilterDateRange({ from: null, to: null, isEmpty: true });
    } else if (existingValueFilter && existingValueFilter.size > 0) {
      setAddFilterSelectedValues(new Set(existingValueFilter));
    } else if (isDateRangeSet(existingDateFilter)) {
      setAddFilterDateRange({
        from: existingDateFilter.from,
        to: existingDateFilter.to,
        ...(existingDateFilter.dynamic
          ? { dynamic: existingDateFilter.dynamic }
          : {}),
      });
    } else if (
      existingNumberFilter &&
      (existingNumberFilter.min !== null || existingNumberFilter.max !== null)
    ) {
      setAddFilterNumberRange({
        min: existingNumberFilter.min,
        max: existingNumberFilter.max,
      });
    }
  }, [
    addFilterColumn,
    addFilterKind,
    addFilterTargetColumn,
    inputFilterMaps,
    resolvedFilterMaps,
    addFilterSourceFilterType,
    setAddFilterValueSearch,
    setAddFilterSelectedValues,
    setAddFilterDateRange,
    setAddFilterNumberRange,
  ]);

  const menuColumnDef = useMemo(
    () =>
      menuColumn ? columnsData?.find((c) => c.column === menuColumn) : null,
    [menuColumn, columnsData]
  );
  const menuColumnType = menuColumnDef?._type ?? null;
  const menuColumnSubType = menuColumnDef?._subType ?? null;

  const {
    formatDateInput: formatDateInputValue,
    parseDateInput: parseDateInputValue,
  } = dateInputUtils;

  const handleStatusChange = useCallback(
    async (row, nextStatus) => {
      if (
        !isStatusEditable ||
        !row?.submissionId ||
        !nextStatus ||
        row.status === nextStatus
      ) {
        return;
      }
      const rowId = row.id;
      const previousStatus = row.status;
      if (statusRequestTimeoutsRef.current[rowId]) {
        clearTimeout(statusRequestTimeoutsRef.current[rowId]);
        delete statusRequestTimeoutsRef.current[rowId];
      }
      setStatusOverrides((prev) => ({
        ...prev,
        [rowId]: nextStatus,
      }));
      setSavingStatusRows((prev) => ({
        ...prev,
        [rowId]: true,
      }));
      setStatusRequestStates((prev) => ({
        ...prev,
        [rowId]: "loading",
      }));
      try {
        const submission = await retrieveSubmission({
          submissionId: row.submissionId,
        }).unwrap();
        const layerIndex = resolveSubmissionLayerIndex(submission, row);
        if (layerIndex < 0) {
          throw new Error("Unable to resolve layer for this report row.");
        }
        await patchSubmission({
          submissionId: row.submissionId,
          patches: [
            {
              path: `state.pricing.layers[${layerIndex}].status`,
              type: "REPLACE",
              value: nextStatus,
            },
            {
              path: `state.pricing.layers[${layerIndex}].statusReason`,
              type: "REPLACE",
              value: null,
            },
          ],
        }).unwrap();
        if (typeof onResultsUpdate === "function") {
          onResultsUpdate((previousData) => {
            const idx = selectedResultIndex;
            if (!previousData?.[idx]?.tableContents) {
              return previousData;
            }
            const updatedTableContents = previousData[idx].tableContents.map(
              (tableRow, tableRowIndex) => {
                const tableRowId = getStatusRowKey(tableRow, tableRowIndex);
                if (tableRowId !== rowId) {
                  return tableRow;
                }
                return {
                  ...tableRow,
                  status: nextStatus,
                };
              }
            );
            return previousData.map((rs, i) =>
              i === idx ? { ...rs, tableContents: updatedTableContents } : rs
            );
          });
        }
        setStatusOverrides((prev) => {
          const next = {
            ...prev,
          };
          delete next[rowId];
          return next;
        });
        setStatusRequestStates((prev) => ({
          ...prev,
          [rowId]: "success",
        }));
        statusRequestTimeoutsRef.current[rowId] = setTimeout(() => {
          setStatusRequestStates((prev) => {
            const next = {
              ...prev,
            };
            delete next[rowId];
            return next;
          });
          delete statusRequestTimeoutsRef.current[rowId];
        }, 3000);
      } catch (error) {
        logger.info(
          "[ReportResults] Failed to update status from report row:",
          {
            row,
            nextStatus,
            error,
          }
        );
        setStatusOverrides((prev) => ({
          ...prev,
          [rowId]: previousStatus,
        }));
        setStatusRequestStates((prev) => ({
          ...prev,
          [rowId]: "error",
        }));
      } finally {
        setSavingStatusRows((prev) => {
          const next = {
            ...prev,
          };
          delete next[rowId];
          return next;
        });
        setActiveStatusEditorRowId((currentRowId) =>
          currentRowId === rowId ? null : currentRowId
        );
      }
    },
    [
      retrieveSubmission,
      patchSubmission,
      onResultsUpdate,
      selectedResultIndex,
      isStatusEditable,
    ]
  );
  const blankGroupLabel = translateLabels(labels.REPORTS_V3_BLANK_GROUP, {
    default: "(blank)",
  });
  const gridColumns = useMemo(() => {
    const submissionLinkField =
      columnsData?.find((c) => c._subType === "SUBMISSION_LINK")?.column ??
      null;
    const createStatusRenderer = (getStatusDisplayValue) => (params) => {
      const statusValue = params.value ?? "";
      const statusLabel =
        getStatusDisplayValue("status", statusValue) ?? statusValue;
      if (!isStatusEditable) {
        return <StatusBadgeV2 status={statusValue} label={statusLabel} />;
      }
      const isEditorActive = activeStatusEditorRowId === params.row.id;
      return (
        <Stack
          className={
            isEditorActive ? "status-cell status-cell-active" : "status-cell"
          }
          direction={"row"}
          spacing={0}
          alignItems={"center"}
          sx={{ width: "100%", minWidth: 0, height: "100%" }}
          onMouseDownCapture={() => {
            setActiveStatusEditorRowId(params.row.id);
          }}
        >
          <Box
            className={"status-display-value"}
            sx={{
              minWidth: 0,
              flex: 1,
              ml: 1,
            }}
          >
            <StatusBadgeV2 status={statusValue} label={statusLabel} />
          </Box>
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            className={"status-edit-controls"}
            sx={{ height: "100%" }}
          >
            <div
              style={
                statusRequestStates[params.row.id] === "error"
                  ? {
                      border: "1px solid red",
                      display: "inline-block",
                      borderRadius: 5,
                    }
                  : {}
              }
            >
              <StatusSelectWithChecks
                layer={params.row}
                status={statusValue}
                disabled={
                  !params.row?.submissionId ||
                  Boolean(savingStatusRows[params.row.id])
                }
                variant={"compact"}
                onChange={(nextStatus) => {
                  void handleStatusChange(params.row, nextStatus);
                }}
              />
            </div>
          </Stack>
          <Box sx={{ ml: 1 }}>
            <RequestIndicator state={statusRequestStates[params.row.id]} />
          </Box>
        </Stack>
      );
    };
    return (
      columnsData
        ?.filter((col) => !isGroupingColumn(col.column))
        .map((col) => {
          const isStatusColumn = col._subType === "ENUM_STATUS_BUTTONS";
          const isTargetPercentage = col._subType === "TARGET_PERCENTAGE";
          const isSubmissionLink = col._subType === "SUBMISSION_LINK";
          const isSubmissionName = col._subType === "SUBMISSION_NAME";
          const isEnumButtons = col._subType === "ENUM_BUTTONS";
          const isPinned = pinnedColumns.includes(col.column);
          const decimalsCount = resolveColumnDecimals(col, columnDecimals);
          const fractionDigitOptions =
            decimalsCount !== undefined && decimalsCount !== null
              ? {
                  minimumFractionDigits: decimalsCount,
                  maximumFractionDigits: decimalsCount,
                }
              : null;
          const columnTypeProps = getColumnTypeProps(col);
          const inputCol = inputColumnView(col);
          const inputTypeProps = getColumnTypeProps(inputCol);
          const baseValueFormatter =
            selectValueFormatter(col, {
              fractionDigitOptions,
              getEnumDisplayValue: getEnumDisplayValueMapped,
            }) ?? defaultTypeValueFormatter(columnTypeProps.type); // the grid keeps an omitted valueFormatter from its saved column state
          const inputDecimalsCount = resolveColumnDecimals(
            inputCol,
            columnDecimals
          );
          const inputFractionDigitOptions =
            inputDecimalsCount !== undefined && inputDecimalsCount !== null
              ? {
                  minimumFractionDigits: inputDecimalsCount,
                  maximumFractionDigits: inputDecimalsCount,
                }
              : null;
          const inputValueFormatter =
            selectValueFormatter(inputCol, {
              fractionDigitOptions: inputFractionDigitOptions,
              getEnumDisplayValue: getInputEnumDisplayValueMapped,
            }) ?? defaultTypeValueFormatter(inputTypeProps.type);
          const rowAwareFormatter = detailRowAware(
            inputValueFormatter,
            baseValueFormatter,
            displayControlColumn
          );
          const rangeGrouped = Object.prototype.hasOwnProperty.call(
            activeRangeGroupings,
            col.column
          );
          const blankGrouped = !rangeGrouped && groupByColumns.has(col.column);
          const valueFormatter = rangeGrouped
            ? rangeGroupedValueFormatter(
                rowAwareFormatter,
                displayControlColumn
              )
            : blankGrouped
            ? blankGroupValueFormatter(
                rowAwareFormatter,
                displayControlColumn,
                blankGroupLabel
              )
            : rowAwareFormatter;
          const identityGetter = (value) => value;
          let outputRenderer = columnTypeProps.renderCell;
          if (isStatusColumn)
            outputRenderer = createStatusRenderer(getEnumDisplayValueMapped);
          if (isTargetPercentage)
            outputRenderer = (params) =>
              renderTargetPriceCell(
                params,
                targetPercentageFractionDigitOptions(col, decimalsCount)
              );
          if (isSubmissionLink) outputRenderer = renderSubmissionIdCell;
          if (isSubmissionName)
            outputRenderer = createSubmissionNameRenderer(submissionLinkField);
          if (isEnumButtons)
            outputRenderer = createEnumChipRenderer(col._options, (val) =>
              getEnumDisplayValueMapped(col.column, val)
            );
          let inputRenderer = null;
          if (inputCol._subType === ENUM_STATUS_BUTTONS_SUB_TYPE)
            inputRenderer = createStatusRenderer(
              getInputEnumDisplayValueMapped
            );
          if (inputCol._subType === ENUM_BUTTONS_SUB_TYPE)
            inputRenderer = createEnumChipRenderer(inputCol._options, (val) =>
              getInputEnumDisplayValueMapped(col.column, val)
            );
          const baseRenderCell = inputRenderer
            ? detailRowAwareRenderer(
                inputRenderer,
                outputRenderer,
                displayControlColumn
              )
            : outputRenderer;
          // Checkbox, chip and link cells draw the raw value, not the formatter's label.
          const renderCell =
            baseRenderCell && blankGrouped
              ? blankGroupRenderer(
                  baseRenderCell,
                  displayControlColumn,
                  blankGroupLabel
                )
              : baseRenderCell;
          const baseWidth =
            columnWidths[col.column] ?? col._width ?? DEFAULT_COLUMN_WIDTH_PX;
          return {
            field: col.column,
            headerName:
              columnNameOverrides[col.column] === null
                ? metadataDisplayNameMap[col.column] || col.column
                : columnNameOverrides[col.column] ||
                  col.displayName ||
                  metadataDisplayNameMap[col.column] ||
                  col.column,
            width: baseWidth,
            headerAlign: "center",
            ...columnTypeProps,
            cellClassName: detailRowAwareCellClassName(
              inputTypeProps,
              columnTypeProps,
              displayControlColumn
            ),
            ...(isStatusColumn && {
              align: "left",
              headerAlign: "left",
            }),
            ...(renderCell && { renderCell }),
            ...(valueFormatter && { valueFormatter }),
            // Always set: the grid keeps an omitted valueGetter from its saved column state.
            valueGetter: rangeGrouped
              ? (value, row) =>
                  rangeGroupedCellValue(
                    row,
                    col.column,
                    displayControlColumn,
                    isDetailRow(row, displayControlColumn)
                      ? inputTypeProps.valueGetter
                      : columnTypeProps.valueGetter
                  )
              : detailRowAware(
                  inputTypeProps.valueGetter ?? identityGetter,
                  columnTypeProps.valueGetter ?? identityGetter,
                  displayControlColumn,
                  identityGetter
                ),
            renderHeader: isPinned ? pinnedHeaderRenderer : headerRenderer,
          };
        }) ?? []
    );
  }, [
    columnsData,
    columnNameOverrides,
    metadataDisplayNameMap,
    columnWidths,
    columnDecimals,
    pinnedColumns,
    getEnumDisplayValueMapped,
    getInputEnumDisplayValueMapped,
    headerRenderer,
    pinnedHeaderRenderer,
    savingStatusRows,
    statusRequestStates,
    activeStatusEditorRowId,
    handleStatusChange,
    isStatusEditable,
    activeRangeGroupings,
    displayControlColumn,
    groupByColumns,
    blankGroupLabel,
  ]);

  const gridColumnsWithExtras = useMemo(() => {
    if (!extraAggregateColumns.length) return gridColumns;
    const extraByFieldName = new Map(
      extraAggregateColumns.map((e) => [e.column, e])
    );
    const existingFieldNameMap = new Map(gridColumns.map((c) => [c.field, c]));
    const result = [];
    for (const col of columnOrder) {
      if (existingFieldNameMap.has(col)) {
        result.push(existingFieldNameMap.get(col));
      } else {
        const extra = extraByFieldName.get(col);
        if (extra) {
          result.push({
            field: extra.column,
            headerName: extra.column,
            width: columnWidths[extra.column] ?? DEFAULT_EXTRA_COLUMN_WIDTH_PX,
            headerAlign: "center",
            type: "string",
            renderHeader: headerRenderer,
          });
        }
      }
    }
    return result;
  }, [
    gridColumns,
    extraAggregateColumns,
    columnOrder,
    columnWidths,
    headerRenderer,
  ]);

  const submissionLinkField = useMemo(
    () =>
      columnsData?.find((c) => c._subType === "SUBMISSION_LINK")?.column ??
      null,
    [columnsData]
  );

  // On wide screens the fixed-width columns don't fill the container, leaving a
  // blank area that reads as an empty column. When fillWidth is on (embedded
  // dashboard), let the last visible column flex to absorb the slack.
  const gridColumnsFilled = useMemo(() => {
    let cols = gridColumnsWithExtras;
    if (fillWidth) {
      let lastVisibleIdx = -1;
      cols.forEach((c, i) => {
        if (columnVisibilityModel[c.field] !== false) lastVisibleIdx = i;
      });
      if (lastVisibleIdx >= 0) {
        cols = cols.map((c, i) =>
          i === lastVisibleIdx ? { ...c, flex: 1, minWidth: c.width ?? 150 } : c
        );
      }
    }
    // Review tab: a trailing "open submission" link icon, mirroring the legacy
    // tab's trailing link cell. Render-only, so it isn't reorderable/hideable.
    if (isLocalLayoutReport && submissionLinkField) {
      cols = [
        ...cols,
        {
          field: "__open_submission__",
          headerName: "",
          width: 56,
          minWidth: 56,
          maxWidth: 56,
          sortable: false,
          filterable: false,
          resizable: false,
          disableColumnMenu: true,
          align: "center",
          headerAlign: "center",
          renderCell: (params) =>
            renderSubmissionIdCell({
              value: params.row[submissionLinkField],
            }),
        },
      ];
    }
    return cols;
  }, [
    fillWidth,
    gridColumnsWithExtras,
    columnVisibilityModel,
    isLocalLayoutReport,
    submissionLinkField,
  ]);

  const isMenuOnLastColumn = useMemo(() => {
    if (!menuColumn) return false;
    return columnOrder[columnOrder.length - 1] === menuColumn;
  }, [menuColumn, columnOrder]);

  columnVisibilityModelRef.current = columnVisibilityModel;

  const handleDnDPinnedChange = useCallback(
    (newPinned, oldPinned) => {
      const changes = userPinChangesRef.current.get(selectedResultIndex) ?? {
        pinned: new Set(),
        unpinned: new Set(),
      };
      const newPinnedSet = new Set(newPinned);
      const oldPinnedSet = new Set(oldPinned);
      for (const col of newPinnedSet) {
        if (!oldPinnedSet.has(col)) {
          changes.pinned.add(col);
          changes.unpinned.delete(col);
        }
      }
      for (const col of oldPinnedSet) {
        if (!newPinnedSet.has(col)) {
          changes.unpinned.add(col);
          changes.pinned.delete(col);
        }
      }
      userPinChangesRef.current.set(selectedResultIndex, changes);
    },
    [selectedResultIndex]
  );

  useColumnDragDrop({
    containerRef,
    columnOrder,
    onReorder: handleColumnReorder,
    pinnedColumns,
    setPinnedColumns,
    onPinnedColumnsChange: handleDnDPinnedChange,
  });

  const handleColumnWidthChange = useCallback(
    (params) => {
      const { colDef, width } = params;
      if (colDef?.field && width) {
        setColumnWidths((prev) => ({
          ...prev,
          [colDef.field]: width,
        }));
      }
    },
    [setColumnWidths]
  );

  useEffect(() => {
    if (
      !apiRef.current ||
      typeof apiRef.current.subscribeEvent !== "function"
    ) {
      return;
    }

    const handleColumnResize = (params) => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      resizeRafRef.current = requestAnimationFrame(() => {
        handleColumnWidthChange(params);
      });
    };

    const unsubscribe = apiRef.current.subscribeEvent(
      "columnResize",
      handleColumnResize
    );
    return () => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [apiRef, handleColumnWidthChange]);

  const selectedAddFilterColumnType = addFilterSourceFilterType;
  const addFilterSourceSummary = addFilterSource.summary;
  const isAddFilterSummaryLoading = filterValuesPending({
    usesInputSummary: addFilterUsesInputSummary,
    inputLoading: isInputSummaryLoading,
    inputSettled: isInputSummarySettled,
    inputFailed: isInputSummaryFailed,
    summaryLoading: isSummaryLoading,
    rerunning: isRerunningReport,
    summary: addFilterSourceSummary,
  });

  const addFilterColumnSummary = useMemo(() => {
    if (isAddFilterSummaryLoading || !addFilterColumn) return null;
    return addFilterSourceSummary;
  }, [isAddFilterSummaryLoading, addFilterSourceSummary, addFilterColumn]);

  const {
    dateBlankCount: addFilterDateBlankCount,
    numberBounds: addFilterNumberBounds,
    histogramBins: addFilterHistogramBins,
  } = useFilterStats({
    column: addFilterColumn,
    columnType:
      selectedAddFilterColumnType === "date"
        ? "date"
        : selectedAddFilterColumnType === "number"
        ? "number"
        : null,
    summary: addFilterColumnSummary,
  });
  const activeFilterGroups = useMemo(
    () =>
      getActiveFilterGroups({
        groupingActive,
        getValueLabel: getEnumDisplayValueMapped,
        columnDisplayNameMap,
        dateFormat: dateFormat || DEFAULT_DATE_FORMAT,
        formatDateValue,
        getDynamicDateLabel: describeDynamicDate,
      }),
    [
      getActiveFilterGroups,
      describeDynamicDate,
      groupingActive,
      getEnumDisplayValueMapped,
      columnDisplayNameMap,
      dateFormat,
    ]
  );

  const addFilterSummaryValues = useMemo(() => {
    if (isAddFilterSummaryLoading) return null;
    if (selectedAddFilterColumnType !== "value") return null;
    if (!addFilterColumn) return null;
    return addFilterSourceSummary;
  }, [
    isAddFilterSummaryLoading,
    addFilterSourceSummary,
    selectedAddFilterColumnType,
    addFilterColumn,
  ]);

  const isAddFilterEnumColumn = addFilterSource.isEnum;
  const {
    values: addFilterColumnValues,
    counts: addFilterValueCounts,
  } = useColumnCounts({
    column: selectedAddFilterColumnType === "value" ? addFilterColumn : null,
    searchQuery: addFilterValueSearch,
    summaryValues: addFilterSummaryValues,
    isEnumColumn: isAddFilterEnumColumn,
    getEnumDisplayValue:
      addFilterKind === FILTER_KIND_INPUT
        ? getInputEnumDisplayValueMapped
        : getEnumDisplayValueMapped,
  });
  const menuColumnFilterChips = useMemo(
    () => chipsByFilterKind(activeFilterGroups, menuColumn),
    [activeFilterGroups, menuColumn]
  );
  const totalActiveFilterCount = activeFilterGroups.reduce(
    (sum, group) =>
      sum +
      group.sections.reduce(
        (sectionSum, section) => sectionSum + section.chips.length,
        0
      ),
    0
  );
  const isAddFilterBinary = addFilterSource.isBinary;
  const toggleAddFilterValue = useCallback(
    (valueOrIds) => {
      setAddFilterSelectedValues((prev) =>
        toggleSelectedValues(prev, valueOrIds, isAddFilterBinary)
      );
    },
    [setAddFilterSelectedValues, isAddFilterBinary]
  );

  const {
    paginationModel,
    visibleRowCount,
    inPageGridHeight,
    displayedRows,
    handlePaginationModelChange,
    handlePrevPage,
    handleNextPage,
    totalRows,
    startRow,
    endRow,
  } = usePagination({
    filteredRows: rows,
    viewMode,
    searchQuery: debouncedSearchQuery,
    metricsCollapsed,
    metricsPanelRef,
    fullscreenContainerRef,
    containerRef,
    toolbarRef,
    paginationRef,
  });

  const handleCloseColumnsPanel = (visibleColumns) => {
    const visibleSet = new Set(visibleColumns);
    const newModel = {};

    sourceColumnsForDisplay.forEach((col) => {
      newModel[col.internalName] = visibleSet.has(col.internalName);
    });

    setColumnVisibilityModel(newModel);
    setColumnsSearchQuery("");
  };

  const orderedPinnedColumns = useMemo(
    () => orderPinnedByColumnOrder(columnOrder, pinnedColumns),
    [columnOrder, pinnedColumns]
  );

  const dataGridStyles = useDataGridStyles({
    hasAddFilterPendingChanges,
    isRerunningReport,
    menuColumn,
    isMenuOnLastColumn,
    truncatedColumnsRef,
    pinnedColumns: orderedPinnedColumns,
    columnWidths,
    gridColumns,
    hasDisplayControl: !!displayControlColumn,
  });
  const handleCloseFiltersPanel = () => {
    if (addFilterColumn && hasAddFilterPendingChanges) {
      resetAddFilterPanel();
    }
    setAddFilterColumn("");
    setFiltersOpen(false);
    setFilterAnchorOverride(null);
    setFilterOpenedFromColumnMenu(false);
  };

  const isWaitingForData = reportData === null;
  const isToolbarDataLoading = isWaitingForData || isRerunningReport;

  const [isGridObfuscated, setIsGridObfuscated] = useState(false);
  useEffect(() => {
    if (isRerunningReport) {
      setIsGridObfuscated(true);
    } else {
      let raf1, raf2;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          setIsGridObfuscated(false);
        });
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
  }, [isRerunningReport]);

  const [canShowSaveDropdown, setCanShowSaveDropdown] = useState(false);

  useEffect(() => {
    if (isToolbarDataLoading) {
      setCanShowSaveDropdown(false);
      return undefined;
    }
    let rafId = null;
    let nestedRafId = null;
    rafId = window.requestAnimationFrame(() => {
      nestedRafId = window.requestAnimationFrame(() => {
        setCanShowSaveDropdown(true);
      });
    });
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (nestedRafId !== null) {
        window.cancelAnimationFrame(nestedRafId);
      }
    };
  }, [isToolbarDataLoading]);

  const MIN_ROWS = 4;
  const renderFilterPanel = ({ open, onClose }) => {
    return (
      <ColumnFilterPanel
        open={open}
        sliderDraggingRef={sliderDraggingRef}
        columnName={addFilterColumn}
        columnDisplayName={getColumnDisplayName(addFilterColumn)}
        filterType={selectedAddFilterColumnType}
        dataType={getColumnDataType(addFilterColumn)}
        valueMultiplier={addFilterSource.valueMultiplier}
        valueSubType={addFilterSource.valueSubType}
        filterKindLabel={
          getFilterKinds(addFilterColumn).length > 1
            ? filterKindLabels(translateLabels, groupingActive)[addFilterKind]
            : undefined
        }
        valuesLoading={isAddFilterSummaryLoading}
        valuesUnavailableMessage={
          addFilterUsesInputSummary && isInputSummaryFailed
            ? translateLabels(labels.OPTION_FILTER_VALUES_UNAVAILABLE, {
                default: "Values could not be loaded",
              })
            : undefined
        }
        onBackToMenu={
          filterOpenedFromColumnMenu
            ? undefined
            : () => {
                if (hasAddFilterPendingChanges) {
                  resetAddFilterPanel();
                } else {
                  setAddFilterColumn("");
                }
              }
        }
        searchQuery={addFilterValueSearch}
        onSearchQueryChange={setAddFilterValueSearch}
        uniqueValues={addFilterColumnValues}
        valueCounts={addFilterValueCounts}
        selectedValues={addFilterSelectedValues}
        onToggleValue={toggleAddFilterValue}
        onClearSelectedValues={() => setAddFilterSelectedValues(new Set())}
        getValueDisplayLabel={(() => {
          if (addFilterSource.isEnum) {
            const toDisplay =
              addFilterKind === FILTER_KIND_INPUT
                ? getInputEnumDisplayValueMapped
                : getEnumDisplayValueMapped;
            return (value) => toDisplay(addFilterColumn, value) ?? value;
          }
          return (value) => value;
        })()}
        minMaxValues={addFilterNumberBounds}
        numberRange={addFilterNumberRange}
        onNumberRangeChange={setAddFilterNumberRange}
        onClearNumberMin={() => {
          setAddFilterNumberRange((prev) => ({
            ...prev,
            min: null,
          }));
        }}
        onClearNumberMax={() => {
          setAddFilterNumberRange((prev) => ({
            ...prev,
            max: null,
          }));
        }}
        histogramBins={addFilterHistogramBins}
        numberBlankCount={addFilterColumnSummary?.blank ?? 0}
        onToggleNumberBlank={() =>
          setAddFilterNumberRange((prev) => ({
            ...prev,
            isEmpty: !prev.isEmpty,
          }))
        }
        onClearNumberBlank={() =>
          setAddFilterNumberRange((prev) => ({
            ...prev,
            isEmpty: false,
          }))
        }
        dateRange={addFilterDateRange}
        onDateRangeChange={setAddFilterDateRange}
        dateFormat={dateFormat}
        dateBlankCount={addFilterDateBlankCount}
        onToggleDateBlank={() =>
          setAddFilterDateRange((prev) => ({
            ...prev,
            isEmpty: !prev.isEmpty,
          }))
        }
        onClearDateBlank={() =>
          setAddFilterDateRange((prev) => ({
            ...prev,
            isEmpty: false,
          }))
        }
        formatDateInputValue={formatDateInputValue}
        parseDateInputValue={parseDateInputValue}
        onClearDateFrom={() =>
          setAddFilterDateRange((prev) => ({
            ...prev,
            from: null,
          }))
        }
        onClearDateTo={() =>
          setAddFilterDateRange((prev) => ({
            ...prev,
            to: null,
          }))
        }
        numberActions={
          selectedAddFilterColumnType === "number"
            ? {
                show: hasAddFilterPendingChanges,
                onSave: () => {
                  handleApplyNewFilter();
                  onClose?.();
                },
                onCancel: () => {
                  resetAddFilterPanel();
                  onClose?.();
                },
              }
            : undefined
        }
        valueActions={
          selectedAddFilterColumnType === "value"
            ? {
                show: hasAddFilterPendingChanges,
                onSave: () => {
                  handleApplyNewFilter();
                  onClose?.();
                },
                onCancel: () => {
                  resetAddFilterPanel();
                  onClose?.();
                },
              }
            : undefined
        }
        dateActions={
          selectedAddFilterColumnType === "date"
            ? {
                show: hasAddFilterPendingChanges,
                onSave: () => {
                  handleApplyNewFilter();
                  onClose?.();
                },
                onCancel: () => {
                  resetAddFilterPanel();
                  onClose?.();
                },
              }
            : undefined
        }
      />
    );
  };

  // to avoid remounting on every render
  const popovers = (
    <React.Fragment>
      <ColumnHeaderMenu
        open={columnMenuOpen}
        anchorEl={columnMenuAnchor}
        fullscreenContainerRef={fullscreenContainerRef}
        menuColumn={menuColumn}
        onClose={handleCloseColumnMenu}
        onSortAsc={handleSortAsc}
        onSortDesc={handleSortDesc}
        onClearSort={handleClearSort}
        isMenuColumnSorted={isMenuColumnSorted}
        onFilterColumn={handleFilterColumn}
        filterKinds={
          menuColumn ? getFilterKinds(menuColumn) : [FILTER_KIND_OUTPUT]
        }
        filterChipsByKind={menuColumnFilterChips}
        groupingActive={groupingActive}
        onRenameColumn={handleRenameColumn}
        onPinColumn={handlePinColumn}
        onUnpinColumn={handleUnpinColumn}
        onHideColumn={handleHideColumn}
        onAddToMetrics={handleAddToMetrics}
        onRemoveFromMetrics={handleRemoveFromMetrics}
        isInMetrics={Boolean(metricsColumnMethodMap[menuColumn])}
        columnMetricMethods={metricsColumnMethodMap[menuColumn] ?? null}
        columnType={menuColumnType}
        columnSubType={menuColumnSubType}
        columnDecimals={columnDecimals}
        onSetColumnDecimals={onSetColumnDecimals}
        isGroupedBy={menuColumn ? groupByColumns.has(menuColumn) : false}
        onGroupByColumn={handleGroupByColumn}
        onUngroupColumn={handleUngroupColumn}
        isRangeGrouped={menuColumn ? isRangeGrouped(menuColumn) : false}
        onRangeGroupBy={openRangeGrouping}
        onEditRangeGrouping={editRangeGrouping}
        onUngroupRangeGrouping={ungroupRangeGrouping}
        summarySelection={
          menuColumn ? summarySelections[menuColumn] : undefined
        }
        onSetSummaryAggregator={handleSetSummaryAggregator}
        onSetSummaryOverride={handleSetSummaryOverride}
        onClearSummary={handleClearSummary}
      />
      <RenameColumnDialog
        open={renameDialogOpen}
        value={renameValue}
        onClose={handleRenameCancel}
        onConfirm={handleRenameConfirm}
        onChange={setRenameValue}
        onRemove={handleRenameRemove}
        removeLabel={translateLabels(labels.RENAME_DIALOG_REMOVE, {
          default: "Remove",
        })}
        hasOverride={Boolean(
          renameField &&
            (columnNameOverrides[renameField] ||
              (columnsData?.find((c) => c.column === renameField)
                ?.displayName &&
                columnsData.find((c) => c.column === renameField)
                  .displayName !==
                  (metadataDisplayNameMap[renameField] || renameField)))
        )}
      />
      <NameDialog
        open={saveAsDialogOpen}
        onClose={handleCloseSaveAsDialog}
        onSave={handleSaveAs}
        reportName={saveAsName}
        onReportNameChange={setSaveAsName}
        reportDescription={saveAsDescription}
        onReportDescriptionChange={setSaveAsDescription}
        error={saveAsError}
        onErrorClear={clearSaveAsError}
        isSaving={isCreatingReport}
        title={translateLabels(labels.REPORT_RESULTS_SAVE_AS_TITLE, {
          default: "Save Report As",
        })}
        saveButtonLabel={translateLabels(labels.REPORT_RESULTS_SAVE_AS_BUTTON, {
          default: "Save",
        })}
        savingButtonLabel={translateLabels(
          labels.REPORT_RESULTS_SAVE_AS_SAVING,
          { default: "Saving..." }
        )}
      />
      <AddAggregateColumnDialog
        open={addColDialog.open}
        title={
          addColDialog.sourceColumn && addColDialog.funcLabel
            ? `${getColumnDisplayName(addColDialog.sourceColumn)} - ${
                addColDialog.funcLabel
              }`
            : ""
        }
        defaultName={
          addColDialog.sourceColumn && addColDialog.funcLabel
            ? `${getColumnDisplayName(addColDialog.sourceColumn)} - ${
                addColDialog.funcLabel
              }`
            : ""
        }
        existingNames={existingColumnNames}
        onSave={handleConfirmAddCol}
        onClose={() =>
          setAddColDialog({
            open: false,
            sourceColumn: null,
            func: null,
            funcLabel: null,
          })
        }
      />
      <RangeGroupingDialog {...rangeGroupingDialogProps} />
    </React.Fragment>
  );
  return (
    <Card
      ref={fullscreenContainerRef}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: viewMode === "fullscreen" ? "none" : 1,

        minHeight: 300,

        ...(viewMode === "fullscreen" && {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
        }),
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          pb: 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Toolbar toolbarRef={toolbarRef}>
          <ClearableSearchField
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={translateLabels(
              labels.REPORT_RESULTS_SEARCH_PLACEHOLDER,
              { default: "search all columns" }
            )}
            disabled={isWaitingForData}
          />
          <ColumnsButton
            availableColumns={sourceColumnsForDisplay}
            showingColumns={sourceColumnsForDisplay.filter(
              (col) => columnVisibilityModel[col.internalName] !== false
            )}
            searchQuery={columnsSearchQuery}
            onClose={handleCloseColumnsPanel}
            onSearchChange={setColumnsSearchQuery}
            container={expanded ? fullscreenContainerRef.current : undefined}
            isMultiTable={isMultiSource}
            tableDisplayNameMap={sourceDisplayNameMap}
            columnDefMap={columnDefMap}
            metricsColumnMethodMap={metricsColumnMethodMap}
            sortModel={sortModel}
            onAddToMetrics={handleAddToMetrics}
            onRemoveFromMetrics={handleRemoveFromMetrics}
            onReorderColumn={
              isLocalLayoutReport ? handleReorderColumnDrop : undefined
            }
            iconOnly={isLocalLayoutReport}
          />
          <FiltersButton
            totalActiveFilterCount={totalActiveFilterCount}
            activeFilterGroups={activeFilterGroups}
            groupingActive={groupingActive}
            columnOptions={columnDefs
              .filter((col) => !isGroupingColumn(col.internalName))
              .map((col) => ({
                column: col.internalName,
                displayName:
                  columnDisplayNameMap[col.internalName] || col.internalName,
                hasFilter: helpers.isColumnFiltered(col.internalName),
                filterKinds: getFilterKinds(col.internalName),
              }))}
            disabled={isWaitingForData}
            container={expanded ? fullscreenContainerRef.current : undefined}
            sliderDraggingRef={sliderDraggingRef}
            onSelectColumn={(column, kind) => setAddFilterColumn(column, kind)}
            onFilterGroupClick={(column, kind) =>
              setAddFilterColumn(column, kind)
            }
            onClose={handleCloseFiltersPanel}
            selectedColumn={addFilterColumn}
            renderFilterPanel={renderFilterPanel}
            open={filtersOpen}
            anchorOverride={filterAnchorOverride}
            onToggle={() => {
              // Filter value lists come from a summary run that is only
              // requested once filters are first opened.
              onRequestSummary?.();
              setFilterAnchorOverride(null);
              setFilterOpenedFromColumnMenu(false);
              setFiltersOpen((prev) => !prev);
            }}
            iconOnly={isLocalLayoutReport}
          />
          <MetricsButton
            metricsItems={metricsItems}
            data={rows}
            metricsCollapsed={metricsCollapsed}
            onToggle={handleToggleMetricsCollapsed}
            disabled={isWaitingForData}
            isLoading={isWaitingForData || isRerunningReport}
            panelContainerRef={metricsPanelRef}
            dateFormat={dateFormat}
            displayControlColumn={displayControlColumn}
          />
          {viewedAsAtLabel && (
            <Tooltip
              title={translateLabels(labels.REPORTS_V3_TREAT_TODAY_AS_HELP, {
                default: TREAT_TODAY_AS_HELP_DEFAULT,
              })}
              arrow
            >
              <Typography
                variant={"body2"}
                data-testid={"treat-today-as-display"}
                sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
              >
                {translateLabels(labels.REPORT_RESULTS_TREAT_TODAY_AS_DISPLAY, {
                  default: "Treating today as {date}",
                  replacements: { date: viewedAsAtLabel },
                })}
              </Typography>
            </Tooltip>
          )}
          <Box
            sx={{
              flex: 1,
            }}
          />{" "}
          {showGroupHeader && (
            <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeDetails}
                    onChange={(e) => setIncludeDetails(e.target.checked)}
                    size={"small"}
                  />
                }
                label={"Detail"}
                sx={{ mr: 0 }}
              />
              {hasDetailRows && (
                <Chip
                  label={hideDetail ? "Show Detail" : "Hide Detail"}
                  size={"small"}
                  onClick={() => setHideDetail((v) => !v)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: hideDetail
                      ? "success.main"
                      : "common.white",
                    color: hideDetail ? "common.white" : "success.main",
                    border: "1px solid",
                    borderColor: "success.main",
                    fontWeight: "medium",
                  }}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeTotal}
                    onChange={(e) => setIncludeTotal(e.target.checked)}
                    size={"small"}
                  />
                }
                label={"Grand Total"}
                sx={{ mr: 0 }}
              />
              {sortModel.length > 0 &&
                groupByColumns.size > 1 &&
                groupByColumns.has(sortModel[0].field) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeSubTotal}
                        onChange={(e) => setIncludeSubTotal(e.target.checked)}
                        size={"small"}
                      />
                    }
                    label={"Sub Total"}
                    sx={{ mr: 0 }}
                  />
                )}
            </Stack>
          )}
          {hasSummarySelections && (
            <Select
              size={"small"}
              value={summaryType}
              onChange={(e) => setSummaryType(e.target.value)}
              variant={"outlined"}
              sx={{ fontSize: "0.8125rem", minWidth: 140 }}
              MenuProps={{
                container: expanded
                  ? fullscreenContainerRef.current
                  : undefined,
              }}
            >
              <MenuItem value={"TOP"}>
                {translateLabels(labels.SUMMARY_TYPE_TOP, {
                  default: "Top Summary",
                })}
              </MenuItem>
              <MenuItem value={"BOTTOM"}>
                {translateLabels(labels.SUMMARY_TYPE_BOTTOM, {
                  default: "Bottom Summary",
                })}
              </MenuItem>
              <MenuItem value={"OFF"}>
                {translateLabels(labels.SUMMARY_TYPE_OFF, {
                  default: "No Summary",
                })}
              </MenuItem>
            </Select>
          )}
          {isLocalLayoutReport ? (
            // Review tab: per-user saved views (this browser). "Default view" is
            // the company config default. No server-side save (shared report).
            <LayoutMenu
              activeName={layoutsState.activeName}
              names={layoutsState.names}
              hasChanges={hasUnsavedChanges}
              onSelect={handleSelectLayout}
              onSaveAs={handleSaveLayoutAs}
              onDelete={handleDeleteLayout}
              container={expanded ? fullscreenContainerRef.current : undefined}
            />
          ) : (
            canShowSaveDropdown &&
            savedReport && (
              <SaveDropdown
                container={
                  expanded ? fullscreenContainerRef.current : undefined
                }
                getChangeDetails={getChangeDetails}
                onSaveAllChanges={handleSaveAllChanges}
                isSaving={isSavingReport}
                onOpenSaveAsDialog={handleOpenSaveAsDialog}
                readOnly={isReadOnly}
                allResults={allResults}
              />
            )
          )}
          <ViewModeToggle
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            disabled={isWaitingForData}
          />
          <DownloadButton
            onClick={handleDownloadXLSX}
            isLoading={downloadLoading}
            isDisabled={isWaitingForData}
            hasError={Boolean(downloadError)}
            error={downloadError}
            children={null}
          />
        </Toolbar>
        <Box ref={metricsPanelRef} />
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {(isWaitingForData || isRerunningReport) && !executionError && (
            <LoadingOverlay />
          )}
          <Box
            ref={containerRef}
            sx={{
              ...dataGridContainerStaticSx,
              ...(viewMode !== "fullscreen" &&
                inPageGridHeight != null && {
                  height: inPageGridHeight,
                  flex: "none",
                }),
              filter: isGridObfuscated ? "blur(2px)" : "none",
              opacity: isGridObfuscated ? 0.6 : 1,
              "& .MuiDataGrid-scrollbar--vertical": {
                display: expanded ? "block" : "none",
              },
            }}
          >
            {reportData === null ? (
              <Box
                sx={{
                  flex: 1,
                }}
              />
            ) : rows.length === 0 ? (
              <Box sx={styles.emptyState}>
                <Typography variant={"body1"} color={"text.secondary"}>
                  {translateLabels(labels.REPORT_RESULTS_EMPTY_STATE, {
                    default: "No data returned. Try adjusting your filters.",
                  })}
                </Typography>
              </Box>
            ) : (
              <DataGrid
                apiRef={apiRef}
                rows={viewMode === "fullscreen" ? rows : displayedRows}
                columns={gridColumnsFilled}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[visibleRowCount || MIN_ROWS, 100]}
                disableRowSelectionOnClick
                disableColumnSorting
                onColumnWidthChange={handleColumnWidthChange}
                hideFooter
                disableColumnMenu
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                columnBufferPx={Number.MAX_SAFE_INTEGER}
                scrollbarSize={0}
                onColumnHeaderClick={handleColumnHeaderClick}
                // The grid has no header key props, so Enter and Space on a
                // header are caught on its root; the grid's own Enter sort is
                // off (disableColumnSorting). Once slotProps.root is set the
                // grid no longer forwards aria- or data- props given to it,
                // so put any of those in here.
                slotProps={{
                  root: {
                    onKeyDown: handleColumnHeaderKeyDown,
                    onKeyUp: handleColumnHeaderKeyUp,
                  },
                }}
                onColumnHeaderContextMenu={handleColumnHeaderContextMenu}
                columnHeaderHeight={
                  56 +
                  (showGroupHeader ? GROUP_HEADER_ROW_HEIGHT_PX : 0) +
                  (showSummaryHeader ? GROUP_HEADER_ROW_HEIGHT_PX : 0)
                }
                initialState={{
                  density: "compact",
                }}
                sx={dataGridStyles}
                getRowId={(row) => row.id}
                getRowClassName={
                  displayControlColumn
                    ? (params) => {
                        const val = params.row[displayControlColumn];
                        if (val === GROUP_ROW_DETAIL)
                          return "row-display-control-detail";
                        if (val === GROUP_ROW_GRAND_TOTAL)
                          return "row-display-control-grand-total";
                        if (val === GROUP_ROW_SUB_TOTAL)
                          return "row-display-control-sub-total";
                        if (val === SUMMARY_ROW)
                          return "row-display-control-summary";
                        if (val === GROUP_ROW_GROUP)
                          return "row-display-control-group";
                        return "";
                      }
                    : undefined
                }
              />
            )}
          </Box>
        </Box>
        {allResults && allResults.length > 1 && (
          <Tabs
            value={selectedResultIndex}
            onChange={(_, newValue) => onResultSelect?.(newValue)}
            variant={"scrollable"}
            scrollButtons={"auto"}
            sx={{ borderTop: 1, borderColor: "divider", minHeight: 36 }}
          >
            {allResults.map((result, i) => {
              const isTabDirty =
                i === selectedResultIndex
                  ? hasUnsavedChanges
                  : dirtySheets.has(i);
              return (
                <Tab
                  key={i}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {result.tableDisplayName || `Result ${i + 1}`}
                      {isTabDirty && (
                        <ChangeCircleIcon
                          sx={{ fontSize: "1.5rem", color: "success.dark" }}
                        />
                      )}
                    </Box>
                  }
                  value={i}
                  sx={{
                    minHeight: 36,
                    py: 0.5,
                    ...(i > 0 && {
                      borderLeft: "1px solid",
                      borderColor: "divider",
                    }),
                  }}
                />
              );
            })}
          </Tabs>
        )}
        <PaginationControls
          paginationRef={paginationRef}
          startRow={startRow}
          endRow={endRow}
          totalRows={totalRows}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
        />
      </CardContent>
      {popovers}
    </Card>
  );
};
export default ReportResults;
